import type { PayoutFundingAvailability } from '../types';
import { authStorage, authSessionVersion } from './authStorage';
import { expireSession, sessionIsIdle, updateSessionState, SessionState } from './sessionLifecycle';
import type { CompanyCreateData } from '../types';
import axios from 'axios';

import type { CompanyTheme, ThemeColors } from '../components/company-theme/companyTheme';
import type {
  AdminBank,
  AdminBankInput,
  BankGuidance,
  BankGuidancePreview,
  BankOption,
  BankSummary,
  AuthResponse,
  BankIdAuthStart,
  BankIdAuthSession,
  User,
  Company,
  Customer,
  CreateCustomerData,
  Project,
  CreateProjectData,
  Contract,
  CreateContractData,
  ContractReplacement,
  Milestone,
  MilestoneMessage,
  MessageThread,
  MessageThreadSummary,
  ConversationMessage,
  MilestoneStartDecision,
  CreateMilestoneData,
  OnboardingStatus,
  ProfileStepData,
  CompanyStepData,
  AdminDashboardStats,
  AdminHomeStats,
  AdminAuditEvent,
  AdminAuditEventQuery,
  ProjectStatusCounts,
  CompanyRegistration,
  AdminProject,
  AdminCustomer,
  Lawyer,
  AdminLawyer,
  Inspector,
  AdminInspector,
  AdminCompanyInvitation,
  AdminProfile,
  AdminProfileUpdate,
  AdminUser,
  InvitationDetails,
  OpenInvitation,
  DocumentRecord,
  DocumentIntegrityDetails,
  FolderTree,
  DmsFolder,
  AdminLibraryCompany,
  ProviderRequest,
  MilestoneBankIdSessionResponse,
  ContractHistoryEntry
} from '../types';
import type { ChangelogDocument } from './changelog';
import type { ProjectStatusInstance } from './projectStatusUtils';
import { rememberAgreementNumbers } from '../utils/agreementIdentity';
import { contractTitle } from '../utils/contractTitle';

export interface AdminPayoutAlertsResponse {
  total: number;
  items: Array<{
    milestoneId: string;
    milestoneNumber: number;
    description: string;
    projectId: string;
    projectNumber: number;
    contractId: string;
    contractNumber: number;
    companyId: string;
    companyName: string;
    amountOre: number;
    blockedAt: string;
    payoutId?: string | null;
    payoutAvailability?: (PayoutFundingAvailability & { overdue: boolean }) | null;
    reason: 'bank_account_sync_missing' | 'payout_failed' | 'payout_blocked';
  }>;
}
export { isRequestTimeout } from '../utils/apiError';

const nestApi = axios.create({
  baseURL: '/v1',
  headers: {
    'Content-Type': 'application/json'
  }
});

export type AdminDailyStatsMetricKey =
  | 'pingReserved'
  | 'paidOut'
  | 'signed'
  | 'signedAfterRot'
  | 'unsignedAmount'
  | 'unsignedCount';

export type AdminDailyStatsMetric = {
  /** flow: what happened that day; balance: what was outstanding at the end of the day. */
  kind: 'flow' | 'balance';
  unit: 'kr' | 'st';
  values: number[];
};

export type AdminDailyStats = {
  days: 7 | 14 | 30;
  timeZone: string;
  /** YYYY-MM-DD, oldest first. */
  dates: string[];
  metrics: Record<AdminDailyStatsMetricKey, AdminDailyStatsMetric>;
};

/** An attestation or objection that has not answered by then is reported as failed, never an endless spinner. */
export const ATTESTATION_TIMEOUT_MS = 30000;

/**
 * Default time limit for a write (POST, PUT, PATCH, DELETE) that sets none of
 * its own (ClickUp 869fgxq45). A save that never answers fails instead of
 * leaving its spinner running for ever.
 */
export const WRITE_TIMEOUT_MS = 30000;

/**
 * Writes that may legitimately take longer keep no time limit: BankID and
 * signing, payments and payouts (Ping, Adyen), contract sending (PDF), admin
 * ledger and debug jobs, and the token refresh.
 */
const LONG_WRITE_PATHS = [
  /bankid/i,
  /\/sign(ing)?(\/|$)/,
  /^\/contracts\/[^/]+\/send$/,
  /payment|payout|ping-|adyen/i,
  /^\/documents/,
  /^\/admin\/debug-database-scenarios/,
  // A refresh that fails logs the user out; a slow one must not.
  /^\/auth\/refresh$/
];

/** Whether a write without its own limit gets WRITE_TIMEOUT_MS. */
export const usesWriteTimeout = (config: {
  method?: string;
  url?: string;
  data?: unknown;
  timeout?: number;
  responseType?: string;
}): boolean => {
  const method = (config.method ?? 'get').toLowerCase();
  if (!['post', 'put', 'patch', 'delete'].includes(method)) return false;
  if (config.timeout) return false;
  // Uploads and file downloads depend on the file's size.
  if (typeof FormData !== 'undefined' && config.data instanceof FormData) return false;
  if (config.responseType === 'blob' || config.responseType === 'arraybuffer') return false;
  const url = config.url ?? '';
  return !LONG_WRITE_PATHS.some(pattern => pattern.test(url));
};

export type TermsTrack = 'pilot' | 'commercial';
export type TermsChangeType = 'initial' | 'informational' | 'material';

export type TermsOfServiceDocument = {
  documentType: 'termsOfService';
  agreementType: 'termsOfService';
  title: 'Användarvillkor';
  heading: 'Användarvillkor för Etappio';
  content: string;
  savedAt: string;
  version: string;
  /** E.g. "Allmänna villkor – Pilot". */
  termsTitle?: string | null;
  termsTrack?: TermsTrack | null;
  changeType?: TermsChangeType | null;
  /** SHA-256 of the exact text of this version. */
  sha256?: string | null;
};

export type TermsVersionView = {
  id: string;
  version: string;
  title: string;
  termsTrack: TermsTrack;
  changeType: TermsChangeType | null;
  language: string;
  publishedAt: string;
  publishedByUserId: string | null;
  sha256: string;
};

export type TermsAcceptanceContext = 'onboarding' | 'registration' | 'invitation' | 'migrated';

export type TermsAcceptance = {
  id: string;
  acceptedAt: string;
  context: TermsAcceptanceContext;
  personaType: string;
  termsVersion: TermsVersionView;
};

export type TermsAcceptanceHistory = {
  acceptances: TermsAcceptance[];
  /** Accepted before versions were recorded. */
  acceptedWithoutVersion: boolean;
  currentVersion: TermsVersionView | null;
};

export type TermsAcceptanceStatus = 'current' | 'previous' | 'unknown' | 'none';

export type TermsReportFilters = {
  status?: TermsAcceptanceStatus | '';
  personaType?: string;
  termsTrack?: TermsTrack | '';
  version?: string;
};

export type TermsAcceptanceReport = {
  currentVersion: TermsVersionView | null;
  versions: Array<TermsVersionView & { latestAcceptedCount: number }>;
  rows: Array<{
    userId: string;
    fullName: string;
    email: string;
    personaType: string;
    companyName: string | null;
    status: TermsAcceptanceStatus;
    latest: {
      version: string;
      title: string;
      termsTrack: TermsTrack;
      acceptedAt: string;
      context: TermsAcceptanceContext;
    } | null;
  }>;
};

const termsReportParams = (filters: TermsReportFilters = {}) =>
  Object.fromEntries(Object.entries(filters).filter(([, value]) => typeof value === 'string' && value.trim() !== ''));

/**
 * Who registered a contractor company (story S3). Admin only: never shown to
 * contractors or other users. `registrarRole` is the role at registration:
 * 'admin' today, 'sales' for an Etappio-säljare (S1). `derived` marks a
 * back-filled row. The link is immutable.
 */
export type CompanyRegistrationSource = 'etappio_staff' | 'self_registered';
export type CompanyRegistrarRole = 'admin' | 'sales';

export type CompanyRegistrationInfo = {
  source: CompanyRegistrationSource | null;
  registeredByUserId: string | null;
  registeredByName: string | null;
  registeredByEmail: string | null;
  registrarRole: CompanyRegistrarRole | null;
  registeredAt: string | null;
  derived: boolean;
};

/** A user id, 'self' (self-registered) or 'missing' (no record). */
export type CompanyRegistrationFilter = string;

export type CompanyRegistrationCounts = {
  registered: number;
  verified: number;
  withProjects: number;
};

export type CompanyRegistrationSummary = {
  registrars: Array<CompanyRegistrationCounts & {
    userId: string;
    fullName: string | null;
    email: string | null;
    /** The registrar's staff role today; null when no longer staff. */
    currentRole: 'admin' | 'support' | null;
  }>;
  selfRegistered: CompanyRegistrationCounts;
  missing: CompanyRegistrationCounts;
  total: number;
};

export type CompanyRegistrationRow = CompanyRegistrationInfo & {
  companyId: string;
  companyNumber: number;
  companyName: string | null;
  organizationNumber: string | null;
  verifiedCompany: string;
  projectCount: number;
};

/** The admin company detail, with who registered it. */
export type AdminCompanyDetail = Company & { registration?: CompanyRegistrationInfo | null };

const companyRegistrationParams = (registeredBy?: CompanyRegistrationFilter) =>
  registeredBy ? { registeredBy } : {};

/**
 * Clients (S8), mirroring contractors: an Etappio staff member creates a client
 * by inviting them, optionally connected to one contractor. `registeredBy` is
 * a staff user id or 'self' (invited by a contractor or on their own).
 */
export type CustomerRegistrationCounts = {
  invited: number;
  activated: number;
  withProjects: number;
};

export type CustomerRegistrationSummary = {
  registrars: Array<CustomerRegistrationCounts & {
    userId: string;
    fullName: string | null;
    email: string | null;
    currentRole: 'admin' | 'support' | null;
  }>;
  self: CustomerRegistrationCounts;
  total: number;
};

export type CustomerRegistrationRow = {
  userId: string;
  fullName: string;
  email: string | null;
  createdAt: string;
  activated: boolean;
  contractorCount: number;
  projectCount: number;
  source: 'etappio_staff' | 'self';
  registeredByUserId: string | null;
  registeredByName: string | null;
  registeredByEmail: string | null;
  registrarRole: CompanyRegistrarRole | null;
  registeredAt: string | null;
};

export type AdminCustomerInvitation = Omit<AdminCompanyInvitation, 'companyName'> & {
  /** The contractor the client is connected to; null for a standalone client. */
  companyName: string | null;
  inviterName?: string | null;
  /** Set when an Etappio staff member invited the client. */
  registeredByUserId: string | null;
  registeredByName: string | null;
  registrarRole: CompanyRegistrarRole | null;
};

export type AdminCustomerInvitationCreate = {
  name: string;
  email: string;
  phone?: string;
  companyId?: string;
};

/** invited: the invitation was sent; linked: an existing account was connected and got a sign-in mail. */
export type AdminCustomerInvitationResult = {
  outcome: 'invited' | 'linked';
  invitation: InvitationDetails;
};

/** A client account not connected to any contractor (S8). */
/**
 * One page of an admin list searched, filtered and sorted on the server
 * (ClickUp 869fjkyz1). `total` counts every match, not just this page.
 */
export type AdminListPage<T> = { data: T[]; total: number; page: number; pageSize: number };

/** What an admin list asks the server for: a page, or (export) every match. */
export type AdminListQuery = {
  page?: number;
  pageSize?: number;
  q?: string;
  sort?: string;
  direction?: 'asc' | 'desc';
  filters?: Record<string, string>;
  /** Export only: the columns in the list's order. */
  columns?: string[];
};

/** A row in Alla beställare: a client record, or a client account without a contractor. */
export type AdminClientRow = Omit<AdminCustomer, 'customerNumber' | 'companyNumber'> & {
  customerNumber: number | null;
  companyNumber: number | null;
  standalone: boolean;
};

const adminListParams = (query: AdminListQuery) => {
  const filters = Object.fromEntries(
    Object.entries(query.filters ?? {}).filter(([, value]) => value.trim() !== '')
  );
  return {
    ...(query.page ? { page: query.page } : {}),
    ...(query.pageSize ? { pageSize: query.pageSize } : {}),
    ...(query.q?.trim() ? { q: query.q.trim() } : {}),
    ...(query.sort ? { sort: query.sort, direction: query.direction ?? 'asc' } : {}),
    ...(Object.keys(filters).length ? { filters: JSON.stringify(filters) } : {}),
    ...(query.columns?.length ? { columns: query.columns.join(',') } : {})
  };
};

export type StandaloneClient = {
  personUserId: string;
  name: string;
  email: string | null;
  phone: string | null;
  address: string | null;
  postalCode: string | null;
  city: string | null;
  verifiedCustomer: 'Verifierad' | 'Delad';
  createdAt: string;
  updatedAt: string;
};

export type ContractorSearchHit = {
  id: string;
  companyNumber: number;
  companyName: string | null;
  organizationNumber: string | null;
};

export type PrivacyPolicyDocument = {
  documentType: 'privacyPolicy';
  title: 'Integritets- och cookiepolicy';
  heading: 'Integritets- och cookiepolicy för Etappio';
  content: string;
  savedAt: string;
  version: string;
};

export type RefundPolicyDocument = {
  documentType: 'refundPolicy';
  title: 'Återbetalnings- och avbokningspolicy';
  heading: 'Återbetalning och avbokning hos Etappio';
  content: string;
  savedAt: string;
  version: string;
};

export type RefundReason =
  | 'withdrawal_right'
  | 'conflict_step_1'
  | 'conflict_level_2'
  | 'other';
export type SellerFeeFundingSource = 'etappio_balance' | 'seller_balance';
export type RefundPolicySettings = {
  conflictStep1SellerFeeTreatment: 'retain' | 'refund';
  conflictStep1SellerFeeFundingSource: SellerFeeFundingSource;
  withdrawalRightSellerFeeFundingSource: SellerFeeFundingSource;
  conflictLevel2SellerFeeTreatment: 'retain' | 'refund';
  conflictLevel2SellerFeeFundingSource: SellerFeeFundingSource;
};

export type DeliveryInformationDocument = {
  documentType: 'deliveryInformation';
  title: 'Leveranser och ansvar';
  heading: 'Leveranser och ansvar på Etappio';
  content: string;
  savedAt: string;
  version: string;
};

export type AdminVersionsResponse = {
  api: {
    version: string;
    commit: string;
    builtAt: string | null;
    repository: string;
    branch: string;
    image: string;
    commitUrl: string | null;
    ciRunUrl: string | null;
    changelogUrl: string | null;
  };
  database: {
    status: 'ok' | 'unavailable';
    migration: { name: string; timestamp: string } | null;
  };
};

export type CheckoutPolicyConsent = {
  accepted: true;
  termsOfServiceVersion: string;
  refundPolicyVersion: string;
  checkoutVersion: string;
};

const unwrapPage = <T>(payload: T[] | { data: T[] }): T[] => {
  return Array.isArray(payload) ? payload : payload.data;
};

const LOCAL_PREFERENCES_KEY = 'etappio.localPreferences';
const NEST_PREFERENCE_KEYS = [
  'theme',
  'language',
  'selectedKpis',
  'welcomeWidgetCollapsed',
  'personWelcomeWidgetCollapsed',
  'personInviteWidgetCollapsed'
] as const;
type NestPreferenceKey = (typeof NEST_PREFERENCE_KEYS)[number];

export type DebugDatabaseScenario = {
  id: string;
  name: string;
  description: string;
  builtIn: boolean;
  createdAt: string | null;
};

export type DebugDatabaseScenariosResponse = {
  debugConfigurable: boolean;
  resetConfirmation: string;
  saveConfirmation: string;
  scenarioStorage?: {
    driver: 'azure_blob' | 'local';
    location: string;
    warning: string | null;
  };
  scenarios: DebugDatabaseScenario[];
};

export type ConflictDurationSettings = {
  inactivityFirstNoticeSeconds: number;
  inactivityReminderSeconds: number;
  inactivityResolutionSeconds: number;
};

export type RuntimeSettingWarning = {
  kind: 'time' | 'simulation';
  key: keyof ConflictDurationSettings | string;
  title: string;
  currentValue: number | boolean;
  recommendedMinimum: number | boolean;
  unit: 'seconds' | null;
  message: string;
  disableInstructions?: string;
  environment: string;
  changedBy: string | null;
  changedAt: string | null;
  changeReason: string | null;
};

export type ConflictSettingsUpdate = ConflictDurationSettings & {
  unsafeProductionConfirmed?: boolean;
  reason?: string;
};

export type ConflictSettings = ConflictDurationSettings & {
  nodeEnv: string;
  testTimeUnitsAvailable: boolean;
  runtimeWarnings: RuntimeSettingWarning[];
  settingDefinitions: Record<keyof ConflictDurationSettings, {
    defaultValue: number;
    recommendedMinimum: number;
    title: string;
    unit: 'seconds';
    message: string;
  }>;
  lastChangedBy: string | null;
  lastChangedAt: string | null;
  lastChangeReason: string | null;
};

export type StagingTestUser = { id: string; fullName: string; email: string | null; personaType: string; isAdmin: boolean; isSupport?: boolean };

export type DebugSettings = {
  bankidStagingTestUserIds?: string[];
  bankidStagingTestUsersConfigurable?: boolean;
  nodeEnv: string;
  debugConfigurable: boolean;
  emailVerificationEnabled: boolean;
  notificationEmailEnabled: boolean;
  bankidOnboardingProvider: 'simulated' | 'tic_test' | 'bankid_prod';
  bankidRequiredAssurance: 'simulated' | 'test' | 'production';
};

export type AdyenRuntimeStatus = {
  requestedMode: 'mock' | 'sandbox';
  effectiveMode: 'mock' | 'sandbox' | 'live';
  checkoutEnvironment: 'test' | 'live';
  cardPaymentsEnabled: boolean;
  debugConfigurable: boolean;
  canUseSandbox: boolean;
  sandboxConfirmation: string;
  reasons: string[];
  gates: {
    nodeEnv: string;
    envMode: string;
    forceMock: boolean;
    allowSandboxApiInTest: boolean;
    adminSelectedSandbox: boolean;
  };
  credentials: {
    balancePlatformApiKeyConfigured: boolean;
    legalEntityManagementApiKeyConfigured: boolean;
    checkoutApiKeyConfigured: boolean;
    merchantAccountConfigured: boolean;
    platformLiableBalanceAccountConfigured: boolean;
    checkoutClientKeyConfigured: boolean;
  };
  endpoints: {
    legalEntityManagementApiBaseUrl: string;
    balancePlatformApiBaseUrl: string;
    checkoutApiBaseUrl: string;
    transfersApiBaseUrl: string;
  };
};

export type PaymentAdyenRuntimeStatus = Pick<AdyenRuntimeStatus, 'effectiveMode'> & {
  checkoutEnvironment: 'test' | 'live';
  cardPaymentsEnabled: boolean;
  checkoutClientKey?: string | null;
  /** The methods a new deposit can use (ClickUp 869fnhhhb); missing from an older API. */
  paymentMethods?: DepositMethodKey[];
};

/** A deposit method Admin switches on and off centrally (ClickUp 869fnhhhb). */
export type DepositMethodKey = 'bank' | 'swish' | 'card';

export type AdminPaymentMethodsOverview = {
  methods: Array<{ method: DepositMethodKey; name: string; enabled: boolean; allowedByEnvironment: boolean }>;
  history: Array<{ method: DepositMethodKey; enabled: boolean; reason: string; changedAt: string; changedBy: string | null }>;
};

export type PaymentProviderSettings = {
  provider: 'adyen' | 'ping';
  mode: 'mock' | 'sandbox' | 'live';
  source: 'admin' | 'environment';
  editable: boolean;
  availableProviders: Array<'adyen' | 'ping'>;
  availableModes: Array<'mock' | 'sandbox' | 'live'>;
  unavailableReason: 'payment_environment_unavailable' | null;
  pingConfigurationIssues?: Array<'deposit_disabled' | 'api_secret_missing' | 'tenant_id_invalid' | 'funding_split_tree_id_invalid'>;
  sandboxConfirmation: string;
};

export type PingPayout = {
  id: string; paymentId: string; milestoneId: string; projectId: string; contractId: string;
  contractNumber: number; milestoneNumber: number; amountOre: number; feeOre: number;
  companyAmountOre: number; currency: string; mode: 'mock' | 'sandbox';
  state: 'waiting_approval' | 'distributing' | 'awaiting_payout' | 'confirmed' | 'insufficient_funds' | 'review_required' | 'failed' | 'returned';
  errorCode: string | null; confirmedAt: string | null; debugBankAvailable: boolean;
  debugBankAccessible?: boolean;
};

export type PingDeposit = {
  paymentId: string;
  projectId: string;
  contractId: string;
  status: 'issuing' | 'awaiting_payment' | 'underfunded' | 'overfunded' | 'awaiting_settlement' | 'funded' | 'review_required';
  amountOre: number;
  currency: string;
  receivedOre: number | null;
  confirmedOre: number;
  mode: 'mock' | 'sandbox';
  notice: { reference: string; bankAccount: Record<string, string>; mock?: boolean } | null;
  checkedAt: string | null;
  payoutAvailable: boolean;
  debugBankAvailable?: boolean;
  errorCode?: string | null;
};

export type PingDebugBank = {
  paymentId: string;
  projectId: string;
  contractId: string;
  currency: string;
  reference: string;
  iban: string;
  amountOre: number | null;
  invoiceAmountOre: number;
  receivedOre: number | null;
  transferStatus: 'not_sent' | 'accepted' | 'uncertain';
  canPay: boolean;
};

export type PingSandboxCheckResult = {
  ok: true;
  environment: 'sandbox';
  checkedAt: string;
  durationMs: number;
  apiVersion: string;
  splitTreeVerified: false;
};

export type AdyenAccount = {
  id: string;
  companyId: string;
  adyenAccountHolderId: string;
  adyenLegalEntityId: string | null;
  adyenBalanceAccountId: string;
  balanceAccountReference: string | null;
  businessLineId: string | null;
  transferInstrumentId: string | null;
  kycStatus: 'pending' | 'in_review' | 'passed' | 'failed';
  payoutsAllowed: boolean;
  capabilities: Record<string, unknown>;
  requiredCapabilities: string[];
  bankAccountMasked: string | null;
  onboardingUrl: string | null;
  onboardingCompletedAt: string | null;
  lastSyncedAt: string | null;
  balanceOre: number;
  balanceSek: number;
  createdAt: string;
  updatedAt: string;
};

export type AdyenLedgerClassification =
  | 'platform_fee'
  | 'adyen_fee'
  | 'transaction_fee'
  | 'payout'
  | 'refund'
  | 'adjustment'
  | 'unknown';

export type AdyenLedgerRow = {
  id: string;
  sourceType: 'api_transaction' | 'report';
  sourceId: string;
  direction: 'incoming' | 'outgoing';
  classification: AdyenLedgerClassification;
  amountOre: number;
  amountSek: number;
  currency: string;
  balanceAccountId: string;
  reference: string | null;
  accountingReference: string | null;
  pspReference: string | null;
  transferId: string | null;
  category: string | null;
  type: string | null;
  status: string | null;
  bookingDate: string | null;
  valueDate: string | null;
  customerId: string | null;
  projectId: string | null;
  contractId: string | null;
  milestoneId: string | null;
  paymentId: string | null;
  rawSource?: Record<string, unknown>;
  customerName?: string | null;
  customerNumber?: number | null;
  projectNumber?: number | null;
  projectDescription?: string | null;
  companyName?: string | null;
  companyNumber?: number | null;
  contractNumber?: number | null;
  milestoneNumber?: number | null;
};

export type AdyenLedgerGroup = {
  projectId: string;
  projectNumber: number | null;
  projectDescription: string | null;
  customerId: string | null;
  customerName: string | null;
  companyName: string | null;
  rows: AdyenLedgerRow[];
  totalIncomingOre: number;
  totalOutgoingOre: number;
};

export type AdyenLedgerResponse = {
  rows: AdyenLedgerRow[];
  groups: AdyenLedgerGroup[];
  unlinked: AdyenLedgerRow[];
};

export type AdyenReportImport = {
  id: string;
  fileName: string;
  reportType: string | null;
  reportReference: string | null;
  balanceAccountId: string | null;
  checksum: string | null;
  status: 'pending' | 'imported' | 'failed';
  importedCount: number;
  updatedCount: number;
  skippedCount: number;
  totalCount: number;
  errorMessage: string | null;
  importedAt: string | null;
  createdAt: string;
};

export type AdyenLedgerQuery = {
  fromDate?: string;
  toDate?: string;
  classification?: AdyenLedgerClassification | 'all';
  linked?: 'all' | 'linked' | 'unlinked';
  projectId?: string;
  customerId?: string;
  balanceAccountId?: string;
};

export type CompanyAccountingReportQuery = {
  fromDate: string;
  toDate: string;
  companyId?: string;
};

export type CompanyAccountingPaymentRow = {
  paymentId: string;
  date: string;
  projectId: string;
  projectNumber: number | null;
  projectDescription: string | null;
  contractId: string | null;
  contractNumber: number | null;
  contractName: string | null;
  milestoneId: string | null;
  milestoneNumber: number | null;
  milestoneDescription: string | null;
  customerName: string | null;
  pspReference: string | null;
  currency: string | null;
  grossAmountOre: number;
  etappioFeeOre: number;
  paymentMethodFeeOre: number;
  adyenFeeOre: number;
  totalDeductionsOre: number;
  netToCompanyOre: number;
  status: string;
};

export type CompanyRotReconciliationRow = {
  scope: 'contract' | 'milestone';
  projectId: string;
  projectNumber: number | null;
  contractId: string;
  contractNumber: number;
  milestoneId: string | null;
  milestoneNumber: number | null;
  description: string;
  grossAmountOre: number;
  plannedRotAmountOre: number;
  afterRotAmountOre: number;
  actualPrincipalPaidOre: number;
  feeBaseOre: number;
  etappioFeeOre: number;
  netLiquidityOre: number;
  allocationMode: 'contract' | 'automatic' | 'manual';
  handledOutsideEtappio: true;
};

export type CompanyAccountingPayoutRow = {
  payoutId: string;
  paymentId: string;
  date: string | null;
  amountOre: number;
  status: string;
  transferReference: string;
  statementDescriptor: string | null;
  projectId: string | null;
  projectNumber: number | null;
  projectDescription: string | null;
  contractId: string | null;
  contractNumber: number | null;
  contractName: string | null;
  milestoneId: string | null;
  milestoneNumber: number | null;
  milestoneDescription: string | null;
  customerName: string | null;
  pspReference: string | null;
  currency: string | null;
};

export type CompanyAccountingRefundRow = {
  refundId: string;
  paymentId: string;
  date: string;
  kind: 'refund' | 'refund_reversal';
  reference: string;
  originalPspReference: string | null;
  refundPspReference: string | null;
  projectId: string;
  projectNumber: number | null;
  projectDescription: string | null;
  contractId: string | null;
  contractNumber: number | null;
  contractName: string | null;
  milestoneId: string | null;
  milestoneNumber: number | null;
  milestoneDescription: string | null;
  customerName: string | null;
  currency: string | null;
  grossRevenueAdjustmentOre: number;
  etappioFeeAdjustmentOre: number;
  paymentMethodFeeAdjustmentOre: number;
  adyenFeeAdjustmentOre: number;
  totalFeeAdjustmentOre: number;
  companyBalanceAdjustmentOre: number;
  status: 'confirmed' | 'reversed';
};

export type CompanyAccountingLedgerRow = {
  id: string;
  date: string | null;
  valueDate: string | null;
  classification: string;
  direction: string;
  amountOre: number;
  reference: string | null;
  accountingReference: string | null;
  pspReference: string | null;
  transferId: string | null;
  balanceAccountId: string;
  currency: string | null;
};

export type CompanyAccountingEntry = {
  id: string;
  verificationNumber: string;
  entryType: string;
  ledgerScope: 'seller';
  ownerCompanyId: string;
  debitAccount: string;
  debitAccountName: string;
  debitSruCode: string | null;
  creditAccount: string;
  creditAccountName: string;
  creditSruCode: string | null;
  amountOre: number;
  vatOre: number | null;
  description: string;
  transactionId: string | null;
  projectId: string | null;
  projectNumber: number | null;
  projectDescription: string | null;
  projectExternalNumber: string | null;
  contractId: string | null;
  contractNumber: number | null;
  contractName: string | null;
  milestoneId: string | null;
  milestoneNumber: number | null;
  milestoneDescription: string | null;
  customerName: string | null;
  adyenReference: string | null;
  sourceEventKey: string | null;
  bookedAt: string;
  createdAt: string;
};

export type CompanyBuyerPrincipalDisposition = {
  id: string;
  kind: 'seller_fee' | 'milestone_completion' | 'refund' | 'other';
  direction: 'decrease' | 'increase';
  amountOre: number;
  sourceType: 'payment' | 'milestone' | 'refund' | 'payment_event' | 'manual';
  sourceId: string;
  sourceReference: string | null;
  description: string;
  occurredAt: string;
  metadata: Record<string, unknown>;
};

export type CompanyBuyerPrincipalLiability = {
  id: string;
  paymentId: string;
  buyerUserId: string;
  buyer: { id: string; fullName: string | null; email: string | null };
  originalPaymentReference: string;
  originalPrincipalOre: number;
  remainingPrincipalOre: number;
  currency: string;
  receivedAt: string;
  status: 'open' | 'settled';
  settledAt: string | null;
  projectId: string | null;
  projectNumber: number | null;
  projectDescription: string | null;
  dispositions: CompanyBuyerPrincipalDisposition[];
};

export type CompanyAccountingReport = {
  reportId: string;
  generatedAt: string;
  period: { fromDate: string; toDate: string };
  company: {
    id: string;
    name: string;
    companyNumber: number;
    organizationNumber: string | null;
  };
  projects: Array<{
    id: string;
    projectNumber: number;
    description: string;
    externalProjectNumber: string | null;
    customerName: string | null;
  }>;
  currency: string | null;
  balanceAccount: {
    id: string;
    reference: string | null;
    currentBalanceOre: number;
  } | null;
  balances: {
    openingBalanceOre: number | null;
    movementOre: number;
    closingBalanceOre: number | null;
    source: string;
  };
  totals: {
    recognizedRevenueOre: number;
    sellerCostsOre: number;
    buyerPrincipalLiabilityOre: number;
    buyerPrincipalLiabilityChangeOre: number;
    grossPaymentsOre: number;
    grossRefundAdjustmentsOre: number;
    netRevenueOre: number;
    etappioFeesOre: number;
    etappioFeeAdjustmentsOre: number;
    paymentMethodFeesOre: number;
    paymentMethodFeeAdjustmentsOre: number;
    adyenFeesOre: number;
    adyenFeeAdjustmentsOre: number;
    totalEarnedOre: number;
    totalDeductedOre: number;
    totalPayoutOre: number;
    pendingPayoutOre: number;
    postPayoutRefundClaimOre: number;
    ledgerIncomingOre: number;
    ledgerOutgoingOre: number;
    unknownLedgerOre: number;
  };
  payments: CompanyAccountingPaymentRow[];
  rotReconciliation: CompanyRotReconciliationRow[];
  refunds: CompanyAccountingRefundRow[];
  payouts: CompanyAccountingPayoutRow[];
  accountingEntries: CompanyAccountingEntry[];
  buyerPrincipalLiabilities: CompanyBuyerPrincipalLiability[];
  unknownLedgerRows: CompanyAccountingLedgerRow[];
  reconciliation: {
    formula: string;
    expectedPendingPayoutOre: number;
    expectedPostPayoutRefundClaimOre: number;
    actualPendingPayoutOre: number;
    actualPostPayoutRefundClaimOre: number;
  };
  completeness: {
    complete: boolean;
    missingVatEntryCount: number;
    unbookedReceivedPaymentCount: number;
    warnings: string[];
  };
};

export type CompanyDashboardFinance = {
  currency: 'SEK';
  reservedAmountOre: number;
  releasedPayoutAmountOre: number;
  completedVolumeOre: number;
};

export type AdyenWebhookSummary = {
  id: string | null;
  type: string | null;
  url: string | null;
  active: boolean | null;
  communicationFormat: string | null;
  description: string | null;
  hasError: boolean | null;
  hasPassword: boolean | null;
  hmacKeyConfigured: boolean;
};

export type AdyenWebhookConfigStatus = {
  targetUrl: string | null;
  etappioBaseUrlConfigured?: boolean;
  publicApiBaseUrlConfigured: boolean;
  managementApiConfigured: boolean;
  merchantIdConfigured: boolean;
  companyIdConfigured: boolean;
  selectedScope: { type: 'merchant' | 'company'; idConfigured: boolean } | null;
  existingWebhooks: AdyenWebhookSummary[];
  matchingWebhook: AdyenWebhookSummary | null;
  recommendedActions: string[];
  confirmations: {
    configure: string;
    test: string;
  };
};

export type AdyenWebhookApplyResult = {
  targetUrl: string;
  scope: { type: 'merchant' | 'company'; idConfigured: boolean };
  webhook: AdyenWebhookSummary;
  hmac: {
    generated: boolean;
    envVarName: string | null;
    valueReturned: boolean;
  };
  nextActions: string[];
};

export type ProjectPaymentEvent = {
  type:
    | 'payment.timeline.updated'
    | 'payment.updated'
    | 'payout.updated'
    | 'project.updated';
  data: {
    projectId: string;
    version: string;
    reason: string;
    paymentId?: string;
    payoutId?: string;
  };
};

export type AdyenLedgerEvent = {
  type: 'adyen.ledger.imported' | 'adyen.ledger.synced';
  data: {
    version: string;
    reason: string;
    imported: number;
    updated: number;
    skipped: number;
    total: number;
    reportImportId?: string;
  };
};

export type AdyenWebhookTestResult = {
  targetUrl: string;
  webhook: AdyenWebhookSummary;
  result: Array<{
    merchantId: string | null;
    status: string | null;
    responseCode: string | null;
    responseTime: string | null;
    output: string | null;
  }>;
};

export type UnknownTransferWebhookDeviation = {
  id: string;
  adyenReference: string;
  eventType: string;
  eventStatus: string;
  eventReference: string;
  occurredAt: string;
  status: 'open' | 'handled';
  rawPayload: Record<string, unknown>;
  handledAt: string | null;
  handledByUserId: string | null;
  handledNote: string | null;
  createdAt: string;
  updatedAt: string;
};

export type AdyenWebhookLog = {
  id: string;
  isAnomaly: boolean;
  anomalyKind:
    | 'unknown_transfer_webhook'
    | 'unknown_configuration_webhook'
    | 'reversed_transfer'
    | 'report_import_failed'
    | 'validation_error'
    | null;
  status: 'logged' | 'open' | 'handled';
  payloadType: string;
  reference: string | null;
  requestId: string | null;
  message: string | null;
  rawPayload: Record<string, unknown>;
  handledAt: string | null;
  handledByUserId: string | null;
  handledNote: string | null;
  createdAt: string;
  updatedAt: string;
};

const isNestPreferenceKey = (key: string): key is NestPreferenceKey => {
  return NEST_PREFERENCE_KEYS.includes(key as NestPreferenceKey);
};

const readLocalPreferences = (): Record<string, any> => {
  if (typeof localStorage === 'undefined') return {};
  try {
    return JSON.parse(localStorage.getItem(LOCAL_PREFERENCES_KEY) || '{}');
  } catch {
    return {};
  }
};

const writeLocalPreference = (key: string, value: any): void => {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(
    LOCAL_PREFERENCES_KEY,
    JSON.stringify({ ...readLocalPreferences(), [key]: value })
  );
};

const getMergedPreferences = async (): Promise<Record<string, any>> => {
  const response = await nestApi.get('/users/me/preferences');
  const localPreferences = Object.fromEntries(
    Object.entries(readLocalPreferences()).filter(([key]) => !isNestPreferenceKey(key))
  );
  return { ...response.data, ...localPreferences };
};

const saveMergedPreference = async (key: string, value: any): Promise<void> => {
  if (!isNestPreferenceKey(key)) {
    writeLocalPreference(key, value);
    return;
  }

  const current = await getMergedPreferences();
  await nestApi.put('/users/me/preferences', {
    theme: current.theme,
    language: current.language,
    selectedKpis: current.selectedKpis,
    welcomeWidgetCollapsed: current.welcomeWidgetCollapsed,
    personWelcomeWidgetCollapsed: current.personWelcomeWidgetCollapsed,
    personInviteWidgetCollapsed: current.personInviteWidgetCollapsed,
    [key]: value
  });
};

const normalizeCompany = (company: Company & { organisationNumber?: string | null }): Company => ({
  ...company,
  organizationNumber: company.organizationNumber ?? company.organisationNumber ?? ''
});

const normalizeCustomer = (customer: Customer): Customer => ({
  ...customer,
  notes: customer.notes ?? '',
  updatedAt: customer.updatedAt ?? customer.createdAt
});

const normalizeSystemInfo = (info: any) => ({
  companyinfo: info.companyinfo ?? info.companyInfo ?? '',
  lawyerinfo: info.lawyerinfo ?? info.lawyerInfo ?? '',
  inspectorinfo: info.inspectorinfo ?? info.inspectorInfo ?? '',
  personinfo: info.personinfo ?? info.personInfo ?? '',
  admininfo: info.admininfo ?? info.adminInfo ?? '',
  extraimportant: info.extraimportant ?? info.extraImportant ?? info.importantInfo ?? '',
  updatedAt: info.updatedAt ?? '',
  updatedBy: info.updatedBy ?? ''
});

const toSystemInfoPayload = (data: {
  companyinfo?: string;
  lawyerinfo?: string;
  inspectorinfo?: string;
  personinfo?: string;
  admininfo?: string;
  extraimportant?: string;
}) => ({
  companyInfo: data.companyinfo,
  lawyerInfo: data.lawyerinfo,
  inspectorInfo: data.inspectorinfo,
  personInfo: data.personinfo,
  adminInfo: data.admininfo,
  extraImportant: data.extraimportant,
  importantInfo: data.extraimportant
});

const oreToSek = (value?: number | null): number => {
  return typeof value === 'number' ? value / 100 : 0;
};

const parseSekToOre = (value?: string): number | undefined => {
  if (!value) return undefined;
  const normalized = value.replace(/\s/g, '').replace(',', '.').replace(/[^0-9.-]/g, '');
  const amount = Number(normalized);
  return Number.isFinite(amount) ? Math.round(amount * 100) : undefined;
};

const normalizeProject = (project: Project & {
  totalContractAmountOre?: number | null;
  paidAmountOre?: number | null;
  remainingAmountOre?: number | null;
}): Project => ({
  ...project,
  companyName: project.companyName ?? null,
  customerNumber: project.customerNumber ?? 0,
  customerName: project.customerName ?? '',
  externalProjectNumber: project.externalProjectNumber ?? '',
  notes: project.notes ?? '',
  totalContractAmount: project.totalContractAmount ?? oreToSek(project.totalContractAmountOre),
  paidAmount: project.paidAmount ?? oreToSek(project.paidAmountOre),
  futurePayments: project.futurePayments ?? oreToSek(project.remainingAmountOre),
  newMessages: project.newMessages ?? 0,
  etappCount: project.etappCount ?? 0,
  startDate: project.startDate ?? '',
  endDate: project.endDate ?? ''
});

const formatSek = (value?: number | null): string => {
  return String(oreToSek(value));
};

const normalizeContract = (contract: Contract & {
  contractAmountOre?: number | null;
  rotAmountOre?: number | null;
  afterRotAmountOre?: number | null;
  paidAmountOre?: number | null;
  remainingAmountOre?: number | null;
}): Contract => ({
  ...contract,
  contractChainId: contract.contractChainId ?? contract.id,
  contractVersion: contract.contractVersion ?? 1,
  versionStatus: contract.versionStatus ?? 'current',
  replacesContractId: contract.replacesContractId ?? null,
  supersededByContractId: contract.supersededByContractId ?? null,
  supersededAt: contract.supersededAt ?? null,
  settlementType: contract.settlementType ?? 'milestones',
  pricingMode: contract.pricingMode ?? 'top_down',
  contractAmount: contract.contractAmount ?? formatSek(contract.contractAmountOre),
  rotAmount: contract.rotAmount ?? formatSek(contract.rotAmountOre),
  afterRotAmount:
    contract.afterRotAmount ??
    formatSek(
      contract.afterRotAmountOre ??
        Number(contract.contractAmountOre ?? 0) - Number(contract.rotAmountOre ?? 0)
    ),
  rotInputMode: contract.rotInputMode ?? 'after_rot',
  paidAmount: contract.paidAmount ?? oreToSek(contract.paidAmountOre),
  remainingAmount: contract.remainingAmount ?? oreToSek(contract.remainingAmountOre),
  companyAttestation: contract.companyAttestation ?? 'not_attested',
  personAttestation: contract.personAttestation ?? 'not_attested',
  draftRejectionMessage: contract.draftRejectionMessage ?? null,
  adminUnlocked: contract.adminUnlocked ?? false,
  adminUnlockedAt: contract.adminUnlockedAt ?? null,
  adminUnlockedBy: contract.adminUnlockedBy ?? null,
  signingStatus: contract.signingStatus ?? 'draft',
  signingProvider: contract.signingProvider ?? null,
  externalDocumentId: contract.externalDocumentId ?? null,
  externalSigningProcessId: contract.externalSigningProcessId ?? null,
  signingUrl: contract.signingUrl ?? null,
  signedDocumentId: contract.signedDocumentId ?? null,
  signingAuditDocumentId: contract.signingAuditDocumentId ?? null,
  signingSentAt: contract.signingSentAt ?? null,
  signingCompletedAt: contract.signingCompletedAt ?? null,
  signingRejectionMessage: contract.signingRejectionMessage ?? null,
  signingRejectedAt: contract.signingRejectedAt ?? null,
  signingRejectedByRole: contract.signingRejectedByRole ?? null,
  signingRejectedByName: contract.signingRejectedByName ?? null
});

const normalizeMilestone = (milestone: Milestone & {
  payoutAmountAfterCreditsOre?: number;
  creditOffsetAmountOre?: number;
  amountOre?: number | null;
  rotAmountOre?: number | null;
  afterRotAmountOre?: number | null;
}): Milestone => ({
  ...milestone,
  ...(milestone.payoutAmountAfterCreditsOre !== undefined ? {
    payoutAmountAfterCredits: oreToSek(milestone.payoutAmountAfterCreditsOre),
    creditOffsetAmount: oreToSek(milestone.creditOffsetAmountOre),
  } : {}),
  amount: milestone.amount ?? oreToSek(milestone.amountOre),
  rotAmount: milestone.rotAmount ?? oreToSek(milestone.rotAmountOre),
  afterRotAmount:
    milestone.afterRotAmount ??
    oreToSek(
      milestone.afterRotAmountOre ??
        Number(milestone.amountOre ?? 0) - Number(milestone.rotAmountOre ?? 0)
    ),
  rotAllocationMode: milestone.rotAllocationMode ?? 'manual',
  endDate: milestone.endDate ?? '',
  companyAttestation: milestone.companyAttestation ?? 'not_attested',
  personAttestation: milestone.personAttestation ?? 'not_attested',
  deliveryStatus: milestone.deliveryStatus ?? 'not_started',
  submittedAt: milestone.submittedAt ?? null,
  disputedAt: milestone.disputedAt ?? null,
  disputeReason: milestone.disputeReason ?? null,
  nextEscalationAt: milestone.nextEscalationAt ?? null,
  conflictStartedAt: milestone.conflictStartedAt ?? null,
  paidAt: milestone.paidAt ?? null,
  fundingRiskStarts: milestone.fundingRiskStarts ?? [],
  workStartedAt: milestone.workStartedAt ?? null,
  workStartedByRole: milestone.workStartedByRole ?? null,
  companyStartDecision: milestone.companyStartDecision ?? null,
  companyStartDecidedAt: milestone.companyStartDecidedAt ?? null,
  startPredecessorIds: milestone.startPredecessorIds ?? [],
  jointlyAttestedAt: milestone.jointlyAttestedAt ?? null,
  itemType: milestone.itemType ?? 'milestone'
});

const normalizeContractReplacement = (replacement: ContractReplacement): ContractReplacement => ({
  ...replacement,
  initiatorRole: replacement.initiatorRole ?? 'company',
  proposalStatus: replacement.proposalStatus ?? 'not_required',
  proposalRespondedAt: replacement.proposalRespondedAt ?? null,
  proposalResponseMessage: replacement.proposalResponseMessage ?? null,
  withdrawnAt: replacement.withdrawnAt ?? null,
  ...(replacement.contract ? { contract: normalizeContract(replacement.contract) } : {}),
  ...(replacement.milestones ? { milestones: replacement.milestones.map(normalizeMilestone) } : {})
});

const normalizeDocument = (
  document: DocumentRecord & {
    filename?: string;
    uploadedByUserId?: string;
    contractId?: string | null;
  },
  contracts: Contract[] = [],
  project?: Project
): DocumentRecord => {
  const contract = document.contractId
    ? contracts.find((candidate) => candidate.id === document.contractId)
    : undefined;
  return {
    ...document,
    companyNumber: document.companyNumber ?? project?.companyNumber ?? 0,
    contractNumber: document.contractNumber ?? contract?.contractNumber ?? null,
    fileName: document.fileName ?? document.filename ?? document.originalName,
    uploadedBy: document.uploadedBy ?? document.uploadedByUserId ?? '',
    uploadedByName: document.uploadedByName ?? document.uploadedByUserId ?? ''
  };
};

const resolveContractId = async (
  projectId: string,
  contractNumber?: number | null
): Promise<string | undefined> => {
  if (contractNumber === undefined || contractNumber === null) return undefined;
  const contracts = await contractApi.getContracts(projectId);
  return contracts.find((contract) => contract.contractNumber === contractNumber)?.id;
};

const documentDownloadDescriptor = async (
  documentId: string
): Promise<{ url: string; expiresAt?: string }> => {
  const descriptor = await nestApi.get(`/documents/${documentId}/download`);
  return descriptor.data;
};

const isAbsoluteUrl = (url: string): boolean => /^https?:\/\//i.test(url);

const triggerBrowserDownload = (url: string, fileName: string): void => {
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', fileName);
  document.body.appendChild(link);
  link.click();
  link.remove();
};

const fetchDocumentBlob = async (documentId: string): Promise<Blob> => {
  const { url } = await documentDownloadDescriptor(documentId);
  if (isAbsoluteUrl(url)) {
    const response = await axios.get(url, {
      responseType: 'blob',
      withCredentials: false,
      headers: {}
    });
    return new Blob([response.data]);
  }
  const localPath = url.startsWith('/v1') ? url.substring('/v1'.length) : url;
  const response = await nestApi.get(localPath, { responseType: 'blob' });
  return new Blob([response.data]);
};

const toProviderPayload = (data: any): Record<string, any> => ({
  ...(data.email && { email: data.email }),
  ...(data.phone !== undefined && { phone: data.phone }),
  ...(data.address !== undefined && { address: data.address }),
  ...(data.postalCode !== undefined && { postalCode: data.postalCode }),
  ...(data.city !== undefined && { city: data.city }),
  ...(data.organizationNumber !== undefined && { organizationNumber: data.organizationNumber }),
  ...(data.lawyerCompanyName !== undefined && { lawyerCompanyName: data.lawyerCompanyName }),
  ...(data.inspectorCompanyName !== undefined && { inspectorCompanyName: data.inspectorCompanyName }),
  ...(data.vatNumber !== undefined && { vatNumber: data.vatNumber }),
  ...(data.notVatRegistered !== undefined && { notVatRegistered: data.notVatRegistered }),
  ...(data.website !== undefined && { website: data.website }),
  ...((data.lawyerPresentation !== undefined || data.inspectorPresentation !== undefined) && {
    presentation: data.lawyerPresentation ?? data.inspectorPresentation
  }),
  ...(data.title !== undefined && { title: data.title }),
  ...(data.experienceYears !== undefined && { experienceYears: data.experienceYears }),
  ...(data.specialties !== undefined && { specialties: data.specialties }),
  ...(data.responseTime !== undefined && { responseTime: data.responseTime }),
  ...(data.hourlyRate !== undefined && { hourlyRate: data.hourlyRate }),
  ...(data.fixedPriceAvailable !== undefined && { fixedPriceAvailable: data.fixedPriceAvailable }),
  ...(data.languages !== undefined && { languages: data.languages }),
  ...(data.geographicArea !== undefined && { geographicArea: data.geographicArea }),
  ...(data.initialMeetingFree !== undefined && { initialMeetingFree: data.initialMeetingFree }),
  ...(data.availability !== undefined && { availability: data.availability }),
  ...(data.publishedToMarketplace !== undefined && { publishedToMarketplace: data.publishedToMarketplace }),
  ...(data.leadTime !== undefined && { leadTime: data.leadTime }),
  ...(data.notes !== undefined && { notes: data.notes })
});

const toProviderUpdatePayload = (data: any): Record<string, any> => ({
  ...(data.phone !== undefined && { phone: data.phone }),
  ...(data.address !== undefined && { address: data.address }),
  ...(data.postalCode !== undefined && { postalCode: data.postalCode }),
  ...(data.city !== undefined && { city: data.city }),
  ...(data.organizationNumber !== undefined && { organizationNumber: data.organizationNumber }),
  ...(data.lawyerCompanyName !== undefined && { lawyerCompanyName: data.lawyerCompanyName }),
  ...(data.inspectorCompanyName !== undefined && { inspectorCompanyName: data.inspectorCompanyName }),
  ...(data.vatNumber !== undefined && { vatNumber: data.vatNumber }),
  ...(data.notVatRegistered !== undefined && { notVatRegistered: data.notVatRegistered }),
  ...(data.website !== undefined && { website: data.website }),
  ...((data.lawyerPresentation !== undefined || data.inspectorPresentation !== undefined) && {
    presentation: data.lawyerPresentation ?? data.inspectorPresentation
  }),
  ...(data.title !== undefined && { title: data.title }),
  ...(data.experienceYears !== undefined && { experienceYears: data.experienceYears }),
  ...(data.specialties !== undefined && { specialties: data.specialties }),
  ...(data.responseTime !== undefined && { responseTime: data.responseTime }),
  ...(data.hourlyRate !== undefined && { hourlyRate: data.hourlyRate }),
  ...(data.fixedPriceAvailable !== undefined && { fixedPriceAvailable: data.fixedPriceAvailable }),
  ...(data.languages !== undefined && { languages: data.languages }),
  ...(data.geographicArea !== undefined && { geographicArea: data.geographicArea }),
  ...(data.initialMeetingFree !== undefined && { initialMeetingFree: data.initialMeetingFree }),
  ...(data.availability !== undefined && { availability: data.availability }),
  ...(data.publishedToMarketplace !== undefined && { publishedToMarketplace: data.publishedToMarketplace }),
  ...(data.leadTime !== undefined && { leadTime: data.leadTime }),
  ...(data.notes !== undefined && { notes: data.notes })
});

const toContractConfigPayload = (data: any): Record<string, any> => ({
  ...(data.id !== undefined && { id: data.id }),
  ...(data.name !== undefined && { name: data.name }),
  ...(data.description !== undefined && { description: data.description }),
  ...(data.is_default !== undefined && { isDefault: data.is_default }),
  ...(data.customer_type !== undefined && { customerType: data.customer_type }),
  ...(data.fee_tiers !== undefined && { feeTiers: data.fee_tiers }),
  ...(data.payment_method_fees !== undefined && { paymentMethodFees: data.payment_method_fees }),
  ...(data.project_fee_per_party !== undefined && { projectFeePerParty: data.project_fee_per_party }),
  ...(data.min_fee_per_party !== undefined && { minFeePerParty: data.min_fee_per_party }),
  ...(data.customer_pays_transaction !== undefined && { customerPaysTransaction: data.customer_pays_transaction }),
  ...(data.customer_pays_project_fee !== undefined && { customerPaysProjectFee: data.customer_pays_project_fee }),
  ...(data.company_pays_transaction !== undefined && { companyPaysTransaction: data.company_pays_transaction }),
  ...(data.company_pays_project_fee !== undefined && { companyPaysProjectFee: data.company_pays_project_fee }),
  ...(data.is_partner_subscription !== undefined && { isPartnerSubscription: data.is_partner_subscription }),
  ...(data.annual_subscription_fee !== undefined && { annualSubscriptionFee: data.annual_subscription_fee }),
  ...(data.partner_covers_customer_fee !== undefined && { partnerCoversCustomerFee: data.partner_covers_customer_fee }),
  ...(data.etappio_cost_per_transaction !== undefined && { etappioCostPerTransaction: data.etappio_cost_per_transaction }),
  ...(data.etappio_target_margin_pct !== undefined && {
    etappioTargetMarginPct: String(data.etappio_target_margin_pct)
  })
});

const toNestProjectPayload = (
  data: CreateProjectData,
  options: { includeContractAmount?: boolean } = {}
) => {
  const contractAmountOre = parseSekToOre(data.contractAmount);
  return {
    // Never '': a project without a Beställare leaves it out (ClickUp 869ff1egy).
    customerId: data.customerId || undefined,
    ...(data.companyId ? { companyId: data.companyId } : {}),
    ...(data.isTest !== undefined ? { isTest: data.isTest } : {}),
    description: data.description,
    startDate: data.startDate || undefined,
    endDate: data.endDate || undefined,
    status: data.status,
    externalProjectNumber: data.externalProjectNumber || undefined,
    notes: data.notes || undefined,
    ...(options.includeContractAmount && contractAmountOre !== undefined
      ? { contractAmountOre }
      : {})
  };
};

const toNestContractPayload = (data: Partial<CreateContractData>) => {
  const contractAmountOre = parseSekToOre(data.contractAmount);
  const afterRotAmountOre = parseSekToOre(data.afterRotAmount);
  return {
    contractType: data.contractType,
    settlementType: data.settlementType,
    parentContractId: data.parentContractId ?? undefined,
    ...(data.resolvesMilestoneId ? { resolvesMilestoneId: data.resolvesMilestoneId } : {}),
    ...(data.referenceMessageId ? { referenceMessageId: data.referenceMessageId } : {}),
    ...(contractAmountOre !== undefined ? { contractAmountOre } : {}),
    ...(afterRotAmountOre !== undefined ? { afterRotAmountOre } : {})
  };
};

const toNestMilestonePayload = (data: Partial<CreateMilestoneData>) => ({
  description: data.description,
  ...(data.contractAmount !== undefined
    ? { contractAmountOre: Math.round(data.contractAmount * 100) } : {}),
  ...(data.rotAmount !== undefined ? { rotAmountOre: Math.round(data.rotAmount * 100) } : {}),
  amountOre: typeof data.amount === 'number' ? Math.round(data.amount * 100) : undefined,
  endDate: data.endDate || undefined
});

const attachAccessToken = (config: any) => {
  if (!config.__skipIdleCheck && authStorage.getItem('accessToken') && sessionIsIdle()) {
    expireSession();
    throw new axios.CanceledError('Session idle timeout');
  }
  config.__authSessionVersion ??= authSessionVersion();
  const token = authStorage.getItem('accessToken');
  if (token && !config.headers?.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  const impersonationToken = authStorage.getItem('impersonationToken');
  if (impersonationToken && !config.__skipImpersonation) {
    config.headers = config.headers ?? {};
    config.headers['X-Etappio-Impersonation'] = impersonationToken;
  }
  return config;
};

let refreshRequest: Promise<AuthResponse> | null = null;
let refreshingToken: string | null = null;

const clearSessionAndRedirect = () => {
  expireSession();
};

const refreshAccessToken = (refreshToken: string) => {
  if (!refreshRequest || refreshingToken !== refreshToken) {
    refreshingToken = refreshToken;
    refreshRequest = nestApi
      .post('/auth/refresh', undefined, {
        headers: { Authorization: `Bearer ${refreshToken}` },
        __skipAuthRefresh: true,
        __skipImpersonation: true
      } as any)
      .then(response => {
        const tokens = response.data as AuthResponse;
        // A late refresh from the previous actor must not replace the new session.
        if (authStorage.getItem('refreshToken') !== refreshToken) return tokens;
        authStorage.setItem('accessToken', tokens.accessToken);
        authStorage.setItem('refreshToken', tokens.refreshToken);
        authStorage.removeItem('token');
        updateSessionState(tokens.session);
        return tokens;
      })
      .finally(() => {
        if (refreshingToken === refreshToken) { refreshRequest = null; refreshingToken = null; }
      });
  }
  return refreshRequest;
};

const handleUnauthorized = async (error: any) => {
  if (error.config?.__authSessionVersion !== undefined && error.config.__authSessionVersion !== authSessionVersion()) return Promise.reject(new axios.CanceledError('Previous actor session'));
  if (error.response?.status !== 401) return Promise.reject(error);
  if (error.config?.url === '/auth/logout') return Promise.reject(error);
  if (error.response?.data?.error?.code === 'SESSION_IDLE_EXPIRED') {
    clearSessionAndRedirect();
    return Promise.reject(error);
  }

  const requestConfig = error.config as any;
  if (
    authStorage.getItem('impersonationToken') &&
    !requestConfig?.__skipImpersonation
  ) {
    authStorage.removeItem('impersonationToken');
    if (!window.location.pathname.startsWith('/admin/profile')) {
      window.location.href = '/admin/profile?impersonation=expired';
    }
    return Promise.reject(error);
  }
  const refreshToken = authStorage.getItem('refreshToken');
  const requestUrl = typeof requestConfig?.url === 'string' ? requestConfig.url : '';
  const isAuthenticationRequest = /^\/auth\/(?:bankid(?:\/|$)|refresh$)/.test(requestUrl);
  if (requestUrl.startsWith('/auth/bankid/')) return Promise.reject(error);
  if (
    !requestConfig ||
    requestConfig.__skipAuthRefresh ||
    requestConfig.__authRetry ||
    isAuthenticationRequest ||
    !refreshToken
  ) {
    clearSessionAndRedirect();
    return Promise.reject(error);
  }

  try {
    const tokens = await refreshAccessToken(refreshToken);
    if (authStorage.getItem('refreshToken') !== tokens.refreshToken) return Promise.reject(error);
    requestConfig.__authRetry = true;
    requestConfig.headers = requestConfig.headers ?? {};
    requestConfig.headers.Authorization = `Bearer ${tokens.accessToken}`;
    return nestApi.request(requestConfig);
  } catch (refreshError) {
    if (authStorage.getItem('refreshToken') === refreshToken) clearSessionAndRedirect();
    return Promise.reject(refreshError);
  }
};

nestApi.interceptors.request.use(attachAccessToken);
nestApi.interceptors.request.use(config => {
  if (usesWriteTimeout(config)) config.timeout = WRITE_TIMEOUT_MS;
  return config;
});

nestApi.interceptors.response.use(
  (response) => {
    if ((response.config as any).__authSessionVersion !== authSessionVersion()) throw new axios.CanceledError('Previous actor session');
    return response;
  },
  handleUnauthorized
);

const parseSseMessage = <T extends { type: string; data: unknown }>(message: string): T | null => {
  const eventLine = message
    .split('\n')
    .find((line) => line.startsWith('event:'));
  const dataLines = message
    .split('\n')
    .filter((line) => line.startsWith('data:'))
    .map((line) => line.slice(5).trimStart());
  if (dataLines.length === 0) return null;
  try {
    return {
      type: eventLine?.slice(6).trim() || 'message',
      data: JSON.parse(dataLines.join('\n'))
    } as T;
  } catch {
    return null;
  }
};

const subscribeSse = <T extends { type: string; data: unknown }>(
  path: string,
  onEvent: (event: T) => void,
  options: { onUnavailable?: () => void } = {}
) => {
  // The stream is closed by cancelling its reader, not with an AbortSignal:
  // some Chrome setups (fetch wrappers in extensions) turn an aborted signal
  // into an unhandled rejection that the dev overlay reports on every
  // unsubscribe — including React re-running the effect in development.
  let closed = false;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  let attempts = 0;
  let currentReader: ReadableStreamDefaultReader<Uint8Array> | null = null;

  const connect = async () => {
    const token = authStorage.getItem('accessToken');
    if (!token || closed) return;
    try {
      const response = await fetch(path, {
        headers: {
          Accept: 'text/event-stream',
          Authorization: `Bearer ${token}`
        }
      });
      if (closed) {
        // Unsubscribed while the request was in flight: close it unread.
        response.body?.cancel().catch(() => undefined);
        return;
      }
      if (!response.ok || !response.body) {
        throw new Error(`SSE unavailable: ${response.status}`);
      }
      attempts = 0;
      const reader = response.body.getReader();
      currentReader = reader;
      reader.closed.catch(() => undefined);
      const decoder = new TextDecoder();
      let buffer = '';
      while (!closed) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const messages = buffer.split(/\n\n/);
        buffer = messages.pop() ?? '';
        messages.forEach((message) => {
          const parsed = parseSseMessage<T>(message);
          if (parsed) onEvent(parsed);
        });
      }
    } catch {
      if (!closed) {
        options.onUnavailable?.();
      }
    }
    if (!closed) {
      const delay = Math.min(30000, 2000 * 2 ** attempts);
      attempts += 1;
      reconnectTimer = setTimeout(connect, delay);
    }
  };

  connect();

  return () => {
    closed = true;
    if (reconnectTimer) clearTimeout(reconnectTimer);
    // Cancelling the reader ends the pending read and closes the connection.
    currentReader?.cancel().catch(() => undefined);
    currentReader = null;
  };
};

const subscribeProjectPaymentEvents = (
  projectId: string,
  onEvent: (event: ProjectPaymentEvent) => void,
  options: { onUnavailable?: () => void } = {}
) => subscribeSse<ProjectPaymentEvent>(`/v1/payments/projects/${projectId}/events`, onEvent, options);

const subscribeAdyenLedgerEvents = (
  onEvent: (event: AdyenLedgerEvent) => void,
  options: { onUnavailable?: () => void } = {}
) => subscribeSse<AdyenLedgerEvent>('/v1/admin/adyen/ledger/events', onEvent, options);

// Auth APIs
export const authApi = {
  session: async (): Promise<SessionState> => {
    const response = await nestApi.get('/auth/session', { __skipImpersonation: true } as any);
    return response.data;
  },

  activity: async (): Promise<SessionState> => {
    const response = await nestApi.post('/auth/activity', undefined, { __skipImpersonation: true } as any);
    return response.data;
  },

  me: async (): Promise<User> => {
    const response = await nestApi.get('/users/me');
    return response.data;
  },

  verifyEmail: async (token: string): Promise<{ verified: boolean }> => {
    const response = await nestApi.post('/auth/verify-email', { token });
    return response.data;
  },

  resendVerificationEmail: async (): Promise<{ accepted: true }> => {
    const response = await nestApi.post('/auth/resend-verification-email', {});
    return response.data;
  },

  logout: async (refreshToken: string): Promise<void> => {
    await nestApi.post('/auth/logout', undefined, {
      headers: { Authorization: `Bearer ${refreshToken}` },
      __skipAuthRefresh: true,
      __skipImpersonation: true,
      __skipIdleCheck: true
    } as any);
  },

  getInvitation: async (id: string): Promise<InvitationDetails> => {
    const response = await nestApi.get(`/onboarding/invitations/${id}`);
    return response.data;
  },

  acceptExistingInvitation: async (
    id: string
  ): Promise<{ success: boolean; linked: boolean; alreadyLinked?: boolean; requiresOnboarding?: boolean }> => {
    const response = await nestApi.post(`/onboarding/invitations/${id}/accept-existing`);
    // Keep newly linked customer/company permissions in the existing BankID session.
    const refreshToken = authStorage.getItem('refreshToken');
    if (refreshToken) await refreshAccessToken(refreshToken);
    return response.data;
  },

  declineExistingInvitation: async (
    id: string
  ): Promise<{ success: boolean; declined: boolean; alreadyLinked?: boolean }> => {
    const response = await nestApi.post(`/onboarding/invitations/${id}/decline-existing`);
    return response.data;
  },

  /** Invitations to the person's verified email that wait for them (869fmx898). */
  getOpenInvitations: async (): Promise<OpenInvitation[]> => {
    const response = await nestApi.get('/onboarding/open-invitations');
    return response.data;
  },

  acceptOpenInvitation: async (
    invitationId: string
  ): Promise<{ success: boolean; linked: boolean; alreadyLinked?: boolean }> => {
    const response = await nestApi.post(`/onboarding/open-invitations/${invitationId}/accept`);
    // Keep the new customer link in the existing BankID session.
    const refreshToken = authStorage.getItem('refreshToken');
    if (refreshToken) await refreshAccessToken(refreshToken);
    return response.data;
  },

  declineOpenInvitation: async (
    invitationId: string
  ): Promise<{ success: boolean; declined: boolean }> => {
    const response = await nestApi.post(`/onboarding/open-invitations/${invitationId}/decline`);
    return response.data;
  }
};

export const impersonationApi = {
  request: async (targetEmail: string): Promise<{
    accepted: true;
    targetEmail: string;
    expiresAt: string;
  }> => {
    const response = await nestApi.post('/impersonation/requests', { targetEmail });
    return response.data;
  },

  activate: async (token: string): Promise<{
    impersonationToken: string;
    expiresAt: string;
    target: { id: string; fullName: string; email: string };
  }> => {
    const response = await nestApi.post(
      '/impersonation/activate',
      { token },
      { __skipImpersonation: true } as any
    );
    return response.data;
  },

  end: async (): Promise<{ ended: true }> => {
    const response = await nestApi.post('/impersonation/end');
    return response.data;
  }
};

// Onboarding APIs
export type AddressSuggestion = { placeId: string; label: string };
export type SelectedAddress = { address: string; postalCode: string; city: string; country: string; complete: boolean };

export const addressesApi = {
  createSession: async (): Promise<string> => {
    const { data } = await nestApi.post<{ sessionToken: string }>('/addresses/session', {}, { timeout: 8000 });
    return data.sessionToken;
  },
  autocomplete: async (input: string, sessionToken: string, signal: AbortSignal): Promise<AddressSuggestion[]> => {
    const { data } = await nestApi.post<AddressSuggestion[]>('/addresses/autocomplete', { input, sessionToken }, { signal, timeout: 8000 });
    return data;
  },
  details: async (placeId: string, sessionToken: string, signal: AbortSignal): Promise<SelectedAddress> => {
    const { data } = await nestApi.post<SelectedAddress>('/addresses/details', { placeId, sessionToken }, { signal, timeout: 8000 });
    return data;
  }
};

export const orgNumberApi = {
  testCapabilities: async (): Promise<{ enabled: boolean }> => {
    const response = await nestApi.get('/organizations/org-number/test/capabilities');
    return response.data;
  },
  generateTestNumber: async (): Promise<{ organizationNumber: string }> => {
    const response = await nestApi.post('/organizations/org-number/test');
    return response.data;
  },
  // Self-registration (onboarding) blur check — boolean only, no account details.
  checkAvailability: async (
    orgNumber: string
  ): Promise<{ available: boolean }> => {
    const response = await nestApi.get(
      '/organizations/org-number/availability',
      { params: { orgNumber } }
    );
    return response.data;
  },
  // Admin blur lookup — returns the existing account so the UI can link to it.
  adminLookup: async (
    orgNumber: string
  ): Promise<{
    exists: boolean;
    accountType: string | null;
    userId: string | null;
    accountId: string | null;
  }> => {
    const response = await nestApi.get('/organizations/org-number/lookup', {
      params: { orgNumber }
    });
    return response.data;
  }
};

export const emailApi = {
  // Admin blur lookup — returns the existing account so the UI can link to it.
  // Pass includeCustomers for customer creation so an email already used by any
  // customer also counts as taken.
  adminLookup: async (
    email: string,
    includeCustomers?: boolean
  ): Promise<{
    exists: boolean;
    accountType: string | null;
    accountId: string | null;
    userId: string | null;
    name: string | null;
  }> => {
    const response = await nestApi.get('/accounts/email/lookup', {
      params: {
        email,
        ...(includeCustomers ? { includeCustomers: true } : {})
      }
    });
    return response.data;
  }
};

export const onboardingApi = {
  getStatus: async (): Promise<OnboardingStatus> => {
    const response = await nestApi.get('/onboarding/status');
    return response.data;
  },

  saveProfile: async (data: ProfileStepData): Promise<void> => {
    const { fullName: _verifiedName, ...contact } = data;
    await nestApi.put('/onboarding/step/profile', contact);
  },

  saveCompany: async (data: CompanyStepData): Promise<void> => {
    await nestApi.put('/onboarding/step/company', data);
  },

  acceptTerms: async (meta?: {
    version?: string;
    versionSavedAt?: string;
    agreementType?: string;
  }): Promise<void> => {
    await nestApi.put('/onboarding/step/terms', { accepted: true, ...meta });
  },

  complete: async (): Promise<void> => {
    await nestApi.post('/onboarding/complete');
    const refreshToken = authStorage.getItem('refreshToken');
    if (refreshToken) await refreshAccessToken(refreshToken);
  }
};

// Browser possession is required for every operation after starting BankID.
export type LocalBankIdDebugActor = {
  id: string;
  fullName: string;
  email: string | null;
  personaType: User['personaType'] | 'builder';
  isAdmin: boolean;
};

export type LocalBankIdDebugCapabilities = {
  enabled: boolean;
  actors: LocalBankIdDebugActor[];
  environment?: 'local' | 'testserver' | 'staging' | 'production';
  allowTic?: boolean;
};

export const localBankIdDebugApi = {
  actors: async (): Promise<LocalBankIdDebugCapabilities> => {
    const response = await nestApi.get('/auth/bankid/debug');
    return response.data;
  },
  start: async (data: { purpose: 'login' | 'register'; actorId?: string; fullName?: string; personaType?: BankIdAuthStart['personaType']; invitationId?: string }): Promise<BankIdAuthSession> => {
    const response = await nestApi.post('/auth/bankid/debug/start', data);
    return response.data;
  }
};

export const bankIdApi = {
  start: async (data: BankIdAuthStart): Promise<BankIdAuthSession> => {
    const response = await nestApi.post('/auth/bankid/start', data);
    return response.data;
  },
  getStatus: async (id: string, sessionToken: string): Promise<BankIdAuthSession> => {
    const response = await nestApi.get(`/auth/bankid/${encodeURIComponent(id)}`, {
      headers: { 'X-BankID-Session': sessionToken }
    });
    return response.data;
  },
  complete: async (id: string, sessionToken: string): Promise<AuthResponse> => {
    const response = await nestApi.post(`/auth/bankid/complete/${encodeURIComponent(id)}`, {}, {
      headers: { 'X-BankID-Session': sessionToken }
    });
    return response.data;
  },
  cancel: async (id: string, sessionToken: string): Promise<void> => {
    await nestApi.post(`/auth/bankid/cancel/${encodeURIComponent(id)}`, {}, {
      headers: { 'X-BankID-Session': sessionToken }
    });
  }
};

// Account contact details; BankID owns the verified name.
export const userApi = {
  /** The signed-in user's own terms acceptances. */
  getMyTermsAcceptances: async (): Promise<TermsAcceptanceHistory> => {
    const response = await nestApi.get('/users/me/terms-acceptances');
    return response.data;
  },

  getProfile: async (): Promise<{
    id: string;
    email: string;
    fullName: string;
    phone: string;
    address: string;
    postalCode: string;
    city: string;
    personaType: string;
    banks?: BankSummary[];
  }> => {
    const response = await nestApi.get('/users/me');
    return response.data;
  },

  updateProfile: async (data: {
    phone: string;
    address: string;
    postalCode: string;
    city: string;
    /** A Privatperson's banks, at least one (ClickUp 869eu6fpm). */
    bankIds?: string[];
  }): Promise<void> => {
    await nestApi.patch('/users/me', data);
  },

};

/** Banks a Privatperson chooses among (ClickUp 869eu6fpm). */
export const banksApi = {
  list: async (): Promise<BankOption[]> => {
    const response = await nestApi.get('/banks');
    return response.data;
  },

  /** The person themself or Admin; a Hantverkare gets 403. */
  setPersonBanks: async (userId: string, bankIds: string[]): Promise<BankSummary[]> => {
    const response = await nestApi.put(`/persons/${userId}/banks`, { bankIds });
    return response.data;
  }
};

// Company APIs
export const companyApi = {
  getCompany: async (): Promise<Company> => {
    const response = await nestApi.get('/companies/me');
    return normalizeCompany(response.data);
  },

  updateCompany: async (data: Partial<Company>): Promise<Company> => {
    const response = await nestApi.patch('/companies/me', data);
    return normalizeCompany(response.data);
  }
};

// Company contract theme (logo and colours). The same calls exist for the
// signed-in company (`/companies/me/theme`) and for admin (`/admin/companies/:id/theme`).
export interface CompanyThemeApi {
  get: () => Promise<CompanyTheme>;
  update: (colors: ThemeColors & { paletteKey: string | null }) => Promise<CompanyTheme>;
  uploadLogo: (file: File, onProgress?: (percent: number) => void) => Promise<CompanyTheme>;
  removeLogo: () => Promise<CompanyTheme>;
}

const companyThemeApiAt = (base: string): CompanyThemeApi => ({
  get: async () => (await nestApi.get(base)).data as CompanyTheme,
  update: async colors => (await nestApi.put(base, colors)).data as CompanyTheme,
  uploadLogo: async (file, onProgress) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await nestApi.post(`${base}/logo`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: event => {
        if (onProgress && event.total) onProgress(Math.round((event.loaded / event.total) * 100));
      }
    });
    return response.data as CompanyTheme;
  },
  removeLogo: async () => (await nestApi.delete(`${base}/logo`)).data as CompanyTheme
});

export const companyThemeApi = {
  forCompany: () => companyThemeApiAt('/companies/me/theme'),
  forAdmin: (companyId: string) => companyThemeApiAt(`/admin/companies/${encodeURIComponent(companyId)}/theme`),
  forContract: async (contractId: string): Promise<CompanyTheme> =>
    (await nestApi.get(`/contracts/${encodeURIComponent(contractId)}/theme`)).data as CompanyTheme,
  /** The logo needs the access token, so it is fetched as a blob. */
  fetchLogo: async (logoUrl: string): Promise<Blob> => {
    const path = logoUrl.startsWith('/v1') ? logoUrl.substring('/v1'.length) : logoUrl;
    const response = await nestApi.get(path, { responseType: 'blob' });
    return response.data as Blob;
  }
};

// A lawyer's or inspector's theme (ClickUp 869fa6m3u): the same calls and
// response as the company theme, for themselves and for admin.
export const providerThemeApi = {
  forSelf: (type: 'lawyer' | 'inspector') => companyThemeApiAt(`/${type === 'lawyer' ? 'lawyers' : 'inspectors'}/me/theme`),
  forAdmin: (type: 'lawyer' | 'inspector', userId: string) =>
    companyThemeApiAt(`/admin/${type === 'lawyer' ? 'lawyers' : 'inspectors'}/${encodeURIComponent(userId)}/theme`),
};

// Lawyer APIs (for lawyer's own profile)
export const lawyerApi = {
  getLawyer: async (): Promise<{
    id: string;
    lawyerNumber: number;
    companyNumber: number;
    userId: string;
    name: string;
    organizationNumber: string;
    lawyerCompanyName: string;
    email: string;
    phone: string;
    vatNumber: string | null;
    notVatRegistered: boolean;
    website: string | null;
    lawyerPresentation: string | null;
    verifiedLawyer: string;
    address: string;
    postalCode: string;
    city: string;
    notes: string;
    createdAt: string;
    updatedAt: string;
  }> => {
    const response = await nestApi.get('/lawyers/me');
    return response.data;
  },

  getMyProjects: async (): Promise<any[]> => {
    const response = await nestApi.get('/lawyers/me/projects');
    return response.data.map(normalizeProject);
  },

  getLinkedCompanies: async (): Promise<any[]> => {
    const response = await nestApi.get('/lawyers/me/companies');
    return response.data;
  },

  updateLawyer: async (data: {
    name?: string;
    organizationNumber?: string;
    lawyerCompanyName?: string;
    address?: string;
    city?: string;
    postalCode?: string;
    phone?: string;
    vatNumber?: string | null;
    notVatRegistered?: boolean;
    email?: string;
    website?: string | null;
    lawyerPresentation?: string | null;
  }): Promise<void> => {
    await nestApi.patch('/lawyers/me', toProviderUpdatePayload(data));
  }
};

// Inspector APIs (for inspector's own profile)
export const inspectorApi = {
  getInspector: async (): Promise<{
    id: string;
    inspectorNumber: number;
    companyNumber: number;
    userId: string;
    name: string;
    organizationNumber: string;
    inspectorCompanyName: string;
    email: string;
    phone: string;
    vatNumber: string | null;
    notVatRegistered: boolean;
    website: string | null;
    inspectorPresentation: string | null;
    verifiedInspector: string;
    address: string;
    postalCode: string;
    city: string;
    notes: string;
    createdAt: string;
    updatedAt: string;
  }> => {
    const response = await nestApi.get('/inspectors/me');
    return response.data;
  },

  getMyProjects: async (): Promise<any[]> => {
    const response = await nestApi.get('/inspectors/me/projects');
    return response.data.map(normalizeProject);
  },

  getLinkedCompanies: async (): Promise<any[]> => {
    const response = await nestApi.get('/inspectors/me/companies');
    return response.data;
  },

  updateInspector: async (data: {
    name?: string;
    organizationNumber?: string;
    inspectorCompanyName?: string;
    address?: string;
    city?: string;
    postalCode?: string;
    phone?: string;
    vatNumber?: string | null;
    notVatRegistered?: boolean;
    email?: string;
    website?: string | null;
    inspectorPresentation?: string | null;
  }): Promise<void> => {
    await nestApi.patch('/inspectors/me', toProviderUpdatePayload(data));
  }
};

// Person APIs (for private persons)
/**
 * Adding a Besiktningsman or Jurist (ClickUp 869fjt7ma): an existing verified
 * account gets the profile at once; otherwise the person is invited.
 */
export type ProviderCreateResult<T> =
  | (T & { outcome?: 'linked' })
  | { outcome: 'invited'; invitation: InvitationDetails };

export type CompanyInviteResult =
  | (InvitationDetails & { outcome?: 'invited' | 'linked' })
  | { outcome: 'already_linked'; companyName: string | null };

/** The same outcomes when a contractor invites a client (ClickUp 869fj4txv). */
export type CustomerInviteResult =
  | (InvitationDetails & { outcome?: 'invited' | 'linked'; projectLinked?: boolean })
  | { outcome: 'already_linked'; customerName: string | null; projectLinked?: boolean };

export const personApi = {
  getInvitations: async (): Promise<InvitationDetails[]> => {
    const response = await nestApi.get('/invitations/companies');
    return response.data;
  },

  getLinkedCompanyInfo: async (): Promise<Array<{
    companyNumber: number;
    companyName: string;
    companyPresentation: string | null;
    isSystemCompany: boolean;
    /** The company's theme (logo and colours), ClickUp 869fgp108. */
    theme?: CompanyTheme | null;
  }>> => {
    const response = await nestApi.get('/persons/me/companies');
    return response.data;
  },

  /** Removes a contractor the person is connected to (ClickUp 869fj4txv). */
  removeCompany: async (companyNumber: number): Promise<void> => {
    await nestApi.delete(`/persons/me/companies/${companyNumber}`);
  },

  /**
   * Invites a contractor. A contractor that is already verified is connected
   * at once (`linked`, no registration link), or was connected before
   * (`already_linked`, nothing sent) — ClickUp 869fj4t2h.
   */
  inviteCompany: async (data: {
    companyName?: string;
    contactPhone?: string;
    contactEmail: string;
  }): Promise<CompanyInviteResult> => {
    const response = await nestApi.post('/invitations/companies', data);
    return response.data;
  },

  resendInvitation: async (id: string): Promise<InvitationDetails> => {
    const response = await nestApi.post(`/invitations/${id}/resend`);
    return response.data;
  },

  getProfile: async (): Promise<{
    name: string;
    email: string;
    phone: string;
    address: string;
    postalCode: string;
    city: string;
  }> => {
    const response = await nestApi.get('/users/me');
    return {
      ...response.data,
      name: response.data.fullName
    };
  },

  updateProfile: async (data: {
    phone: string;
    address: string;
    postalCode: string;
    city: string;
  }): Promise<void> => {
    await nestApi.patch('/users/me', {
      phone: data.phone,
      address: data.address,
      postalCode: data.postalCode,
      city: data.city
    });
  },

  getProjects: async (): Promise<any[]> => {
    const response = await nestApi.get('/persons/me/projects');
    return response.data.map(normalizeProject);
  },

  getProjectCompany: async (projectId: string): Promise<Company> => {
    const response = await nestApi.get(`/persons/me/projects/${projectId}/company`);
    return response.data;
  },

  getPendingContracts: async (): Promise<Array<{
    projectId: string;
    projectNumber: number;
    contractId: string;
    contractNumber: number;
    /** Agreement name; null while the contract has none (869fnhht3). */
    contractName: string | null;
    contractAmount: string;
    draftStatus: string;
    notices?: Array<{ id: string; type: "comment" | "rejection"; message: string; authorName: string | null; byRole: string | null; createdAt: string }>;
  }>> => {
    const response = await nestApi.get('/persons/me/pending-contracts');
    return response.data;
  }
};

// Customer APIs
export const customerApi = {
  // Returns every customer, verified or not. The project pickers list the
  // unverified ones as disabled options; the API rejects them at project creation.
  getCustomers: async (): Promise<Customer[]> => {
    const response = await nestApi.get('/companies/me/customers', {
      params: { limit: 500 }
    });
    return unwrapPage<Customer>(response.data).map(normalizeCustomer);
  },

  getCustomer: async (id: string): Promise<Customer> => {
    const response = await nestApi.get(`/companies/me/customers/${id}`);
    return normalizeCustomer(response.data);
  },

  createCustomer: async (data: CreateCustomerData): Promise<Customer> => {
    const response = await nestApi.post('/companies/me/customers', data);
    return normalizeCustomer(response.data);
  },

  updateCustomer: async (id: string, data: CreateCustomerData): Promise<Customer> => {
    const response = await nestApi.patch(`/companies/me/customers/${id}`, data);
    return normalizeCustomer(response.data);
  },

  deleteCustomer: async (id: string): Promise<void> => {
    await nestApi.delete(`/companies/me/customers/${id}`);
  },

  inviteCustomer: async (data: {
    customerName?: string;
    customerEmail: string;
    customerPhone?: string;
    /** Invited from a project without a Beställare (ClickUp 869ff1egy). */
    projectId?: string;
  }): Promise<CustomerInviteResult> => {
    const response = await nestApi.post('/invitations/customers', data);
    return response.data;
  },

  getCustomerInvitations: async (): Promise<InvitationDetails[]> => {
    const response = await nestApi.get('/invitations/customers');
    return response.data;
  },

  resendInvitation: async (id: string): Promise<InvitationDetails> => {
    const response = await nestApi.post(`/invitations/${id}/resend`);
    return response.data;
  }
};

// Project APIs
export const projectApi = {
  getProjects: async (): Promise<Project[]> => {
    const response = await nestApi.get('/projects', { params: { limit: 500 } });
    return unwrapPage<Project>(response.data).map(normalizeProject);
  },

  getProject: async (id: string): Promise<Project> => {
    const response = await nestApi.get(`/projects/${id}`);
    return normalizeProject(response.data);
  },

  getProjectParties: async (id: string): Promise<any> => {
    const response = await nestApi.get(`/projects/${id}/parties`);
    return response.data;
  },

  getNextProjectNumber: async (customerId?: string, companyId?: string): Promise<{ nextProjectNumber: number }> => {
    const response = await nestApi.get('/projects/next-number', {
      params: customerId ? { customerId } : companyId ? { companyId } : undefined
    });
    return response.data;
  },

  createProject: async (data: CreateProjectData): Promise<Project> => {
    const response = await nestApi.post(
      '/projects',
      toNestProjectPayload(data, { includeContractAmount: true })
    );
    return normalizeProject(response.data);
  },

  /**
   * "Dra tillbaka avtalet för att byta beställare" (ClickUp 869fmxjgt): the
   * sent, unsigned agreements become drafts and the Beställare is released.
   */
  releaseCustomer: async (id: string): Promise<{ released: boolean; contractIds: string[] }> => {
    const response = await nestApi.post(`/projects/${id}/customer/release`);
    return response.data;
  },

  updateProject: async (id: string, data: CreateProjectData): Promise<Project> => {
    const response = await nestApi.patch(`/projects/${id}`, toNestProjectPayload(data));
    return normalizeProject(response.data);
  }
};

const normalizeAccountingEntry = (entry: any) => ({
  ...entry,
  amountSek: entry.amountSek ?? oreToSek(entry.amountOre),
  vatSek: entry.vatSek ?? (entry.vatOre == null ? null : oreToSek(entry.vatOre))
});

const normalizeAdminTransactionDetail = (detail: any) => ({
  ...detail,
  ...(Array.isArray(detail?.entries)
    ? { entries: detail.entries.map(normalizeAccountingEntry) }
    : {}),
  ...(Array.isArray(detail?.accountingEntries)
    ? { accountingEntries: detail.accountingEntries.map(normalizeAccountingEntry) }
    : {})
});

const normalizeAccountingSummaryRow = (row: any) => ({
  ...row,
  debit: row.debit ?? oreToSek(row.debitOre) ?? 0,
  credit: row.credit ?? oreToSek(row.creditOre) ?? 0,
  net: row.net ?? oreToSek(row.netOre) ?? 0
});

const companyAccountingQueryString = (params: CompanyAccountingReportQuery) => {
  const qs = new URLSearchParams();
  qs.append('fromDate', params.fromDate);
  qs.append('toDate', params.toDate);
  if (params.companyId) qs.append('companyId', params.companyId);
  return qs.toString();
};

export const companyAccountingApi = {
  getReport: async (params: CompanyAccountingReportQuery): Promise<CompanyAccountingReport> => {
    const response = await nestApi.get(`/accounting/company-report?${companyAccountingQueryString(params)}`);
    return {
      ...response.data,
      accountingEntries: Array.isArray(response.data?.accountingEntries)
        ? response.data.accountingEntries.map(normalizeAccountingEntry)
        : [],
      rotReconciliation: Array.isArray(response.data?.rotReconciliation)
        ? response.data.rotReconciliation
        : []
    };
  },

  exportCsv: async (params: CompanyAccountingReportQuery): Promise<{ blob: Blob; filename: string }> => {
    const response = await nestApi.get(`/accounting/company-report/export.csv?${companyAccountingQueryString(params)}`, {
      responseType: 'blob'
    });
    const ts = new Date().toISOString().slice(0, 10);
    return { blob: response.data, filename: `etappio-foretagsredovisning-${ts}.csv` };
  }
};

export const companyDashboardApi = {
  getFinance: async (): Promise<CompanyDashboardFinance> => {
    const response = await nestApi.get('/payments/dashboard/finance');
    return response.data;
  }
};

// Admin APIs
export type PingWebhookReceipt = {
  endpoint_id: string; event_id: string; context_id: string; received_at: string;
  event_type: string; event_version: string; state: string; attempts: number;
  last_error: string | null; next_attempt_at: string; lease_until: string | null;
  payment_id: string | null; payout_id: string | null; reference: string | null;
  recipient_id?: string | null; merchant_payout_ids?: string[];
};
export type PingWebhookHistory = {
  history: { id: string; recorded_at: string; state: string; attempts: number; error_code: string | null }[];
  requests: { id: string; requested_at: string; requested_by: string; reason: string }[];
};

export const adminApi = {
  getSessionSettings: async (): Promise<{ idleTimeoutMinutes: number }> => {
    const response = await nestApi.get('/admin/session-settings');
    return response.data;
  },
  updateSessionSettings: async (idleTimeoutMinutes: number): Promise<{ idleTimeoutMinutes: number }> => {
    const response = await nestApi.put('/admin/session-settings', { idleTimeoutMinutes });
    return response.data;
  },
  getPingWebhooks: async (params: { state?: string; type?: string; reference?: string; contextId?: string; cursor?: string } = {}): Promise<{ items: PingWebhookReceipt[]; nextCursor: string | null }> => {
    const response = await nestApi.get('/admin/ping/webhooks', { params });
    return response.data;
  },
  getPingWebhookHistory: async (row: PingWebhookReceipt): Promise<PingWebhookHistory> => {
    const response = await nestApi.get(`/admin/ping/webhooks/${encodeURIComponent(row.endpoint_id)}/${encodeURIComponent(row.event_id)}/history`);
    return response.data;
  },
  reconsiderPingWebhook: async (row: PingWebhookReceipt, reason: string): Promise<{ queued: boolean }> => {
    const response = await nestApi.post(`/admin/ping/webhooks/${encodeURIComponent(row.endpoint_id)}/${encodeURIComponent(row.event_id)}/reconsider`, { reason });
    return response.data;
  },
  createCompany: async (data: CompanyCreateData): Promise<{ company: Company; invitation: InvitationDetails }> => {
    const response = await nestApi.post('/admin/companies', data);
    return { ...response.data, company: normalizeCompany(response.data.company) };
  },

  resendCompanyInvitation: async (id: string): Promise<InvitationDetails> => {
    const response = await nestApi.post(`/invitations/${id}/resend`);
    return response.data;
  },

  getCompany: async (id: string): Promise<AdminCompanyDetail> => {
    const response = await nestApi.get(`/admin/companies/${id}`);
    return normalizeCompany(response.data);
  },

  getCompanyRegistrationSummary: async (): Promise<CompanyRegistrationSummary> => {
    const response = await nestApi.get('/admin/company-registrations/summary');
    return response.data;
  },

  getCompanyRegistrations: async (registeredBy?: CompanyRegistrationFilter): Promise<CompanyRegistrationRow[]> => {
    const response = await nestApi.get('/admin/company-registrations', {
      params: companyRegistrationParams(registeredBy)
    });
    return response.data;
  },

  exportCompanyRegistrations: async (registeredBy?: CompanyRegistrationFilter): Promise<{ blob: Blob; filename: string }> => {
    const response = await nestApi.get('/admin/company-registrations/export.csv', {
      params: companyRegistrationParams(registeredBy),
      responseType: 'blob'
    });
    const ts = new Date().toISOString().slice(0, 10);
    return { blob: response.data, filename: `etappio-registreringar-${ts}.csv` };
  },

  createCustomerInvitation: async (data: AdminCustomerInvitationCreate): Promise<AdminCustomerInvitationResult> => {
    const response = await nestApi.post('/admin/customer-invitations', data);
    return response.data;
  },

  getAllCustomerInvitations: async (): Promise<AdminCustomerInvitation[]> => {
    const response = await nestApi.get('/admin/customer-invitations');
    return response.data;
  },

  getCustomerRegistrationSummary: async (): Promise<CustomerRegistrationSummary> => {
    const response = await nestApi.get('/admin/customer-registrations/summary');
    return response.data;
  },

  getCustomerRegistrations: async (registeredBy?: CompanyRegistrationFilter): Promise<CustomerRegistrationRow[]> => {
    const response = await nestApi.get('/admin/customer-registrations', {
      params: companyRegistrationParams(registeredBy)
    });
    return response.data;
  },

  exportCustomerRegistrations: async (registeredBy?: CompanyRegistrationFilter): Promise<{ blob: Blob; filename: string }> => {
    const response = await nestApi.get('/admin/customer-registrations/export.csv', {
      params: companyRegistrationParams(registeredBy),
      responseType: 'blob'
    });
    const ts = new Date().toISOString().slice(0, 10);
    return { blob: response.data, filename: `etappio-registreringar-bestallare-${ts}.csv` };
  },

  getStandaloneClients: async (): Promise<StandaloneClient[]> => {
    const response = await nestApi.get('/admin/standalone-clients');
    return response.data;
  },

  /** Contractor picker: a term of at least two characters, at most ten hits. */
  searchContractors: async (q: string): Promise<ContractorSearchHit[]> => {
    const response = await nestApi.get('/admin/contractor-search', { params: { q } });
    return response.data;
  },

  getVersions: async (): Promise<AdminVersionsResponse> => {
    const response = await nestApi.get('/admin/versions');
    return response.data;
  },

  // BAS 2026 bookkeeping
  getBasAccounts: async (): Promise<
    Array<{
      number: string;
      name: string;
      kind: string;
      description: string;
      sruCode: string | null;
    }>
  > => {
    const response = await nestApi.get('/admin/accounting/accounts');
    return response.data;
  },

  getAccountingOwners: async (): Promise<
    Array<{ id: string; companyName: string; organizationNumber: string | null }>
  > => {
    const response = await nestApi.get('/admin/accounting/owners');
    return response.data;
  },

  getAccountingEntries: async (params?: {
    testFilter?: 'all' | 'ordinary' | 'test' | 'unallocated';
    scope?: 'etappio' | 'seller';
    fromDate?: string;
    toDate?: string;
    account?: string;
    type?: string;
    projectId?: string;
    transactionId?: string;
    ownerCompanyId?: string;
  }): Promise<any[]> => {
    const qs = new URLSearchParams();
    if (params?.testFilter) qs.append('testFilter', params.testFilter);
    if (params?.scope) qs.append('scope', params.scope);
    if (params?.fromDate) qs.append('fromDate', params.fromDate);
    if (params?.toDate) qs.append('toDate', params.toDate);
    if (params?.account) qs.append('account', params.account);
    if (params?.type) qs.append('type', params.type);
    if (params?.projectId) qs.append('projectId', params.projectId);
    if (params?.transactionId) qs.append('transactionId', params.transactionId);
    if (params?.ownerCompanyId) qs.append('ownerCompanyId', params.ownerCompanyId);
    const response = await nestApi.get(`/admin/accounting/entries?${qs.toString()}`);
    return response.data.map(normalizeAccountingEntry);
  },

  getAccountingClassificationSummary: async (params: { scope?: 'etappio' | 'seller'; fromDate?: string; toDate?: string; account?: string; type?: string; ownerCompanyId?: string }): Promise<any> => {
    const response = await nestApi.get('/admin/accounting/classification-summary', { params });
    return response.data;
  },

  getAccountingSummary: async (params?: {
    testFilter?: 'all' | 'ordinary' | 'test' | 'unallocated';
    scope?: 'etappio' | 'seller';
    fromDate?: string;
    toDate?: string;
    ownerCompanyId?: string;
  }): Promise<any[]> => {
    const qs = new URLSearchParams();
    if (params?.testFilter) qs.append('testFilter', params.testFilter);
    if (params?.scope) qs.append('scope', params.scope);
    if (params?.fromDate) qs.append('fromDate', params.fromDate);
    if (params?.toDate) qs.append('toDate', params.toDate);
    if (params?.ownerCompanyId) qs.append('ownerCompanyId', params.ownerCompanyId);
    const response = await nestApi.get(`/admin/accounting/summary?${qs.toString()}`);
    return response.data.map(normalizeAccountingSummaryRow);
  },

  exportAccounting: async (params: {
    format: 'csv' | 'sie4';
    scope?: 'etappio' | 'seller';
    fromDate?: string;
    toDate?: string;
    account?: string;
    type?: string;
    ownerCompanyId?: string;
  }): Promise<{ blob: Blob; filename: string }> => {
    const qs = new URLSearchParams();
    qs.append('format', params.format);
    if (params.scope) qs.append('scope', params.scope);
    if (params.fromDate) qs.append('fromDate', params.fromDate);
    if (params.toDate) qs.append('toDate', params.toDate);
    if (params.account) qs.append('account', params.account);
    if (params.type) qs.append('type', params.type);
    if (params.ownerCompanyId) qs.append('ownerCompanyId', params.ownerCompanyId);
    const response = await nestApi.get(`/admin/accounting/export?${qs.toString()}`, { responseType: 'blob' });
    const ts = new Date().toISOString().slice(0, 10);
    const filename = params.format === 'sie4' ? `etappio-bokforing-${ts}.se` : `etappio-bokforing-${ts}.csv`;
    return { blob: response.data, filename };
  },

  getAuditEvents: async (params?: AdminAuditEventQuery): Promise<AdminAuditEvent[]> => {
    const qs = new URLSearchParams();
    if (params?.customerId) qs.append('customerId', params.customerId);
    if (params?.companyId) qs.append('companyId', params.companyId);
    if (params?.contractId) qs.append('contractId', params.contractId);
    if (params?.userId) qs.append('userId', params.userId);
    if (params?.projectId) qs.append('projectId', params.projectId);
    if (params?.milestoneId) qs.append('milestoneId', params.milestoneId);
    if (params?.paymentId) qs.append('paymentId', params.paymentId);
    if (params?.eventType) qs.append('eventType', params.eventType);
    if (params?.fromDate) qs.append('fromDate', params.fromDate);
    if (params?.toDate) qs.append('toDate', params.toDate);
    if (params?.limit) qs.append('limit', String(params.limit));
    if (params?.offset) qs.append('offset', String(params.offset));
    const query = qs.toString();
    const response = await nestApi.get(`/admin/audit-events${query ? `?${query}` : ''}`);
    return response.data;
  },

  exportAuditEvents: async (params?: AdminAuditEventQuery): Promise<{ blob: Blob; filename: string }> => {
    const qs = new URLSearchParams();
    if (params?.customerId) qs.append('customerId', params.customerId);
    if (params?.companyId) qs.append('companyId', params.companyId);
    if (params?.contractId) qs.append('contractId', params.contractId);
    if (params?.userId) qs.append('userId', params.userId);
    if (params?.projectId) qs.append('projectId', params.projectId);
    if (params?.milestoneId) qs.append('milestoneId', params.milestoneId);
    if (params?.paymentId) qs.append('paymentId', params.paymentId);
    if (params?.eventType) qs.append('eventType', params.eventType);
    if (params?.fromDate) qs.append('fromDate', params.fromDate);
    if (params?.toDate) qs.append('toDate', params.toDate);
    if (params?.limit) qs.append('limit', String(params.limit));
    const response = await nestApi.get(`/admin/audit-events/export.csv?${qs.toString()}`, { responseType: 'blob' });
    const ts = new Date().toISOString().slice(0, 10);
    return { blob: response.data, filename: `etappio-revisionsspar-${ts}.csv` };
  },

  getAdminTransactionDetail: async (id: string): Promise<any> => {
    const response = await nestApi.get(`/admin/transactions/${id}`);
    return normalizeAdminTransactionDetail(response.data);
  },

  refundAdminTransaction: async (
    id: string,
    input: {
      amountOre?: number;
      idempotencyKey: string;
      refundReason?:
        | 'withdrawal_right'
        | 'conflict_step_1'
        | 'conflict_level_2'
        | 'other';
      sellerFeeTreatment?: 'retain' | 'refund';
      sellerFeeFundingSource?: 'etappio_balance' | 'seller_balance';
      note?: string;
    }
  ): Promise<any> => {
    const response = await nestApi.post(`/admin/transactions/${id}/actions`, {
      action: 'refund',
      ...input
    });
    return response.data;
  },

  getTransactions: async (): Promise<any[]> => {
    const response = await nestApi.get('/admin/transactions');
    return response.data;
  },

  getAdyenAccounts: async (): Promise<any[]> => {
    const response = await nestApi.get('/admin/adyen-accounts');
    return response.data;
  },

  getCompanyAdyenAccount: async (companyId: string): Promise<AdyenAccount | null> => {
    const response = await nestApi.get(`/admin/companies/${companyId}/adyen-account`);
    return response.data ?? null;
  },

  refreshCompanyAdyenAccount: async (companyId: string): Promise<AdyenAccount> => {
    const response = await nestApi.post(`/admin/companies/${companyId}/adyen-account/refresh`);
    return response.data;
  },

  getAdyenLedger: async (params?: AdyenLedgerQuery): Promise<AdyenLedgerResponse> => {
    const qs = new URLSearchParams();
    if (params?.fromDate) qs.append('fromDate', params.fromDate);
    if (params?.toDate) qs.append('toDate', params.toDate);
    if (params?.classification && params.classification !== 'all') {
      qs.append('classification', params.classification);
    }
    if (params?.linked && params.linked !== 'all') qs.append('linked', params.linked);
    if (params?.projectId) qs.append('projectId', params.projectId);
    if (params?.customerId) qs.append('customerId', params.customerId);
    if (params?.balanceAccountId) qs.append('balanceAccountId', params.balanceAccountId);
    const query = qs.toString();
    const response = await nestApi.get(`/admin/adyen/ledger${query ? `?${query}` : ''}`);
    return response.data;
  },

  exportAdyenLedger: async (params?: AdyenLedgerQuery): Promise<{ blob: Blob; filename: string }> => {
    const qs = new URLSearchParams();
    if (params?.fromDate) qs.append('fromDate', params.fromDate);
    if (params?.toDate) qs.append('toDate', params.toDate);
    if (params?.classification && params.classification !== 'all') {
      qs.append('classification', params.classification);
    }
    if (params?.linked && params.linked !== 'all') qs.append('linked', params.linked);
    if (params?.projectId) qs.append('projectId', params.projectId);
    if (params?.customerId) qs.append('customerId', params.customerId);
    if (params?.balanceAccountId) qs.append('balanceAccountId', params.balanceAccountId);
    const response = await nestApi.get(`/admin/adyen/ledger/export.csv?${qs.toString()}`, { responseType: 'blob' });
    const ts = new Date().toISOString().slice(0, 10);
    return { blob: response.data, filename: `etappio-adyen-ledger-${ts}.csv` };
  },

  syncAdyenLedger: async (params: {
    confirmation: 'SYNC_ADYEN_LEDGER';
    fromDate?: string;
    toDate?: string;
    balanceAccountId?: string;
    limit?: number;
  }): Promise<{ imported: number; updated: number; skipped: number; total: number }> => {
    const response = await nestApi.post('/admin/adyen/ledger/sync', params);
    return response.data;
  },

  importAdyenLedgerReport: async (params: {
    reportText: string;
    reportName?: string;
    balanceAccountId?: string;
  }): Promise<{ imported: number; updated: number; skipped: number; total: number }> => {
    const response = await nestApi.post('/admin/adyen/ledger/import-report', params);
    return response.data;
  },

  getAdyenLedgerReportImports: async (): Promise<AdyenReportImport[]> => {
    const response = await nestApi.get('/admin/adyen/ledger/report-imports');
    return response.data;
  },

  subscribeAdyenLedgerEvents: (
    onEvent: (event: AdyenLedgerEvent) => void,
    options?: { onUnavailable?: () => void }
  ): (() => void) => subscribeAdyenLedgerEvents(onEvent, options),

  getUnknownTransferWebhooks: async (status?: 'open' | 'handled'): Promise<UnknownTransferWebhookDeviation[]> => {
    const response = await nestApi.get(`/admin/adyen/unknown-transfer-webhooks${status ? `?status=${status}` : ''}`);
    return response.data;
  },

  handleUnknownTransferWebhook: async (id: string, note?: string | null): Promise<UnknownTransferWebhookDeviation> => {
    const response = await nestApi.patch(`/admin/adyen/unknown-transfer-webhooks/${id}/handle`, { note });
    return response.data;
  },

  getAdyenWebhookLogs: async (status?: 'logged' | 'open' | 'handled'): Promise<AdyenWebhookLog[]> => {
    const response = await nestApi.get(`/admin/adyen/webhook-logs${status ? `?status=${status}` : ''}`);
    return response.data;
  },

  handleAdyenWebhookLog: async (id: string, note?: string | null): Promise<AdyenWebhookLog> => {
    const response = await nestApi.patch(`/admin/adyen/webhook-logs/${id}/handle`, { note });
    return response.data;
  },

  getDashboardStats: async (): Promise<AdminDashboardStats> => {
    const response = await nestApi.get('/admin/dashboard');
    return response.data;
  },

  retryPayout: async (milestoneId: string, payoutId: string): Promise<{ status: string; payoutId: string }> => {
    const response = await nestApi.post('/admin/payouts/retry', { milestoneId, payoutId }, { timeout: 30000 });
    return response.data;
  },
  getPayoutAlerts: async (projectId?: string): Promise<AdminPayoutAlertsResponse> => {
    const response = await nestApi.get('/admin/payout-alerts', { params: projectId ? { projectId } : undefined });
    return response.data;
  },

  getHomeStats: async (includeTests?: boolean): Promise<AdminHomeStats> => {
    const response = await nestApi.get('/admin/home-stats', ...(includeTests === undefined ? [] : [{ params: { includeTests } }]));
    return response.data;
  },

  /** Daily series for the admin home charts: 2 × days Stockholm days, oldest first. */
  getDailyStats: async (days: 7 | 14 | 30 = 30, includeTests?: boolean): Promise<AdminDailyStats> => {
    const response = await nestApi.get('/admin/home/daily-stats', { params: { days, ...(includeTests !== undefined ? { includeTests } : {}) } });
    return response.data;
  },

  getProjectsByStatus: async (fromDate?: string, toDate?: string, includeTests?: boolean): Promise<ProjectStatusCounts> => {
    const params = new URLSearchParams();
    if (includeTests !== undefined) params.append('includeTests', String(includeTests));
    if (fromDate) params.append('fromDate', fromDate);
    if (toDate) params.append('toDate', toDate);
    const query = params.toString();
    const response = await nestApi.get(`/admin/projects-by-status${query ? `?${query}` : ''}`);
    return response.data;
  },

  getCompanies: async (): Promise<CompanyRegistration[]> => {
    const response = await nestApi.get('/admin/companies');
    return response.data;
  },

  getAllProjects: async (): Promise<AdminProject[]> => {
    const response = await nestApi.get('/projects', { params: { limit: 500 } });
    return unwrapPage<AdminProject>(response.data).map(normalizeProject);
  },

  getAllCustomers: async (params?: { q?: string; limit?: number; companyNumber?: number }): Promise<AdminCustomer[]> => {
    const qs = new URLSearchParams();
    if (params?.q) qs.append('q', params.q);
    if (params?.limit) qs.append('limit', String(params.limit));
    if (params?.companyNumber) qs.append('companyNumber', String(params.companyNumber));
    const query = qs.toString();
    const response = await nestApi.get(`/admin/customers${query ? `?${query}` : ''}`);
    return response.data;
  },

  getCustomer: async (id: string): Promise<AdminCustomer> => {
    const response = await nestApi.get(`/admin/customers/${encodeURIComponent(id)}`);
    return response.data;
  },

  /** One page of Alla beställare, client accounts without a contractor included. */
  getCustomerPage: async (query: AdminListQuery): Promise<AdminListPage<AdminClientRow>> => {
    const response = await nestApi.get('/admin/customers/page', { params: adminListParams(query) });
    return response.data;
  },

  /** Every client that matches the list, as CSV. */
  exportCustomers: async (query: AdminListQuery): Promise<{ blob: Blob; filename: string }> => {
    const response = await nestApi.get('/admin/customers/export', { params: adminListParams(query), responseType: 'blob' });
    return { blob: response.data, filename: `admin_customers_${new Date().toISOString().slice(0, 10)}.csv` };
  },

  /** One page of Alla hantverkare. */
  getCompanyPage: async (query: AdminListQuery): Promise<AdminListPage<Company>> => {
    const response = await nestApi.get('/admin/companies/page', { params: adminListParams(query) });
    return { ...response.data, data: response.data.data.map(normalizeCompany) };
  },

  /** Every contractor that matches the list, as CSV. */
  exportCompanies: async (query: AdminListQuery): Promise<{ blob: Blob; filename: string }> => {
    const response = await nestApi.get('/admin/companies/export', { params: adminListParams(query), responseType: 'blob' });
    return { blob: response.data, filename: `companies_${new Date().toISOString().slice(0, 10)}.csv` };
  },

  getCompanyList: async (params?: { q?: string; limit?: number }): Promise<Company[]> => {
    const qs = new URLSearchParams();
    if (params?.q) qs.append('q', params.q);
    if (params?.limit) qs.append('limit', String(params.limit));
    const query = qs.toString();
    const response = await nestApi.get(`/admin/companies${query ? `?${query}` : ''}`);
    return response.data.map(normalizeCompany);
  },

  updateCompany: async (id: string, data: Partial<Company>): Promise<Company> => {
    const response = await nestApi.patch(`/admin/companies/${id}`, data);
    return normalizeCompany(response.data);
  },

  getProfile: async (): Promise<AdminProfile> => {
    const response = await nestApi.get('/users/me');
    return response.data;
  },

  updateProfile: async (data: AdminProfileUpdate): Promise<void> => {
    await nestApi.patch('/users/me', data);
  },


  /** Blocks a contractor ('Blockerad') or lifts the block ('Ovärifierad'; the
   *  server then sets Verifierad if onboarding is complete). Verifierad itself
   *  is only reached through onboarding (BankID + verified e-mail). */
  setCompanyVerification: async (
    id: string,
    verifiedCompany: 'Blockerad' | 'Ovärifierad'
  ): Promise<void> => {
    await nestApi.put(`/admin/companies/${id}/status`, { verifiedCompany });
  },

  createCustomer: async (data: {
    name: string;
    email: string;
    phone?: string;
    address?: string;
    postalCode?: string;
    city?: string;
    notes?: string;
    companyId: string;
  }): Promise<AdminCustomer> => {
    const response = await nestApi.post('/admin/customers', data);
    return normalizeCustomer(response.data) as AdminCustomer;
  },

  updateCustomer: async (id: string, data: {
    name: string;
    email: string;
    phone?: string;
    address?: string;
    postalCode?: string;
    city?: string;
    notes?: string;
    companyId: string;
  }): Promise<AdminCustomer> => {
    const response = await nestApi.patch(`/admin/customers/${id}`, data);
    return normalizeCustomer(response.data) as AdminCustomer;
  },

  getAllLawyers: async (): Promise<AdminLawyer[]> => {
    const response = await nestApi.get('/lawyers');
    return response.data;
  },

  getNextLawyerNumber: async (): Promise<{ nextLawyerNumber: number }> => {
    const lawyers = await adminApi.getAllLawyers();
    return { nextLawyerNumber: lawyers.length + 1 };
  },

  createLawyer: async (data: {
    email: string;
    phone?: string;
    organizationNumber?: string;
    lawyerCompanyName?: string;
    vatNumber?: string;
    website?: string;
    lawyerPresentation?: string;
    verifiedLawyer?: string;
    title?: string;
    experienceYears?: number;
    specialties?: string[];
    responseTime?: string;
    hourlyRate?: string;
    fixedPriceAvailable?: boolean;
    languages?: string[];
    geographicArea?: string;
    initialMeetingFree?: boolean;
    availability?: boolean;
    leadTime?: string;
    address?: string;
    postalCode?: string;
    city?: string;
    notes?: string;
    companyId?: string;
  }): Promise<ProviderCreateResult<AdminLawyer>> => {
    const response = await nestApi.post('/lawyers', toProviderPayload(data));
    return response.data;
  },

  updateLawyer: async (id: string, data: Partial<Lawyer> & { companyId?: string }): Promise<AdminLawyer> => {
    const response = await nestApi.patch(`/lawyers/${id}`, toProviderUpdatePayload(data));
    return response.data;
  },

  getAllCompanyInvitations: async (): Promise<AdminCompanyInvitation[]> => {
    const response = await nestApi.get('/admin/company-invitations');
    return response.data;
  },

  getAllInspectors: async (): Promise<AdminInspector[]> => {
    const response = await nestApi.get('/inspectors');
    return response.data;
  },

  getNextInspectorNumber: async (): Promise<{ nextInspectorNumber: number }> => {
    const inspectors = await adminApi.getAllInspectors();
    return { nextInspectorNumber: inspectors.length + 1 };
  },

  createInspector: async (data: {
    email: string;
    phone?: string;
    organizationNumber?: string;
    inspectorCompanyName?: string;
    vatNumber?: string;
    website?: string;
    inspectorPresentation?: string;
    verifiedInspector?: string;
    title?: string;
    experienceYears?: number;
    specialties?: string[];
    responseTime?: string;
    hourlyRate?: string;
    fixedPriceAvailable?: boolean;
    languages?: string[];
    geographicArea?: string;
    initialMeetingFree?: boolean;
    availability?: boolean;
    leadTime?: string;
    address?: string;
    postalCode?: string;
    city?: string;
    notes?: string;
    companyId?: string;
  }): Promise<ProviderCreateResult<AdminInspector>> => {
    const response = await nestApi.post('/inspectors', toProviderPayload(data));
    return response.data;
  },

  /** Open invitations to a Besiktningsman or Jurist (ClickUp 869fjt7ma). */
  getProviderInvitations: async (type: 'lawyer' | 'inspector'): Promise<InvitationDetails[]> => {
    const response = await nestApi.get(`/invitations/providers/${type}`);
    return response.data;
  },

  resendProviderInvitation: async (id: string): Promise<InvitationDetails> => {
    const response = await nestApi.post(`/invitations/${id}/resend`);
    return response.data;
  },

  updateInspector: async (id: string, data: Partial<Inspector> & { companyId?: string }): Promise<AdminInspector> => {
    const response = await nestApi.patch(`/inspectors/${id}`, toProviderUpdatePayload(data));
    return response.data;
  },

  getPreferences: async (): Promise<Record<string, any>> => {
    return getMergedPreferences();
  },

  savePreference: async (key: string, value: any): Promise<void> => {
    await saveMergedPreference(key, value);
  },

  getSystemInfo: async (): Promise<{
    companyinfo: string;
    lawyerinfo: string;
    inspectorinfo: string;
    personinfo: string;
    admininfo: string;
    extraimportant: string;
    updatedAt: string;
    updatedBy: string;
  }> => {
    const response = await nestApi.get('/admin/system-info');
    return normalizeSystemInfo(response.data);
  },

  updateSystemInfo: async (data: {
    companyinfo?: string;
    lawyerinfo?: string;
    inspectorinfo?: string;
    personinfo?: string;
    admininfo?: string;
    extraimportant?: string;
  }): Promise<void> => {
    await nestApi.put('/admin/system-info', toSystemInfoPayload(data));
  },

  getAgreementInfo: async (): Promise<{
    personAgreement: { agreementType: string; content: string; versions: Array<{ version: string; content: string; savedBy: string; savedByName: string; savedAt: string }> };
    companyAgreement: { agreementType: string; content: string; versions: Array<{ version: string; content: string; savedBy: string; savedByName: string; savedAt: string }> };
    privacyPolicy: { agreementType: string; content: string; versions: Array<{ version: string; content: string; savedBy: string; savedByName: string; savedAt: string }> };
    refundPolicy: { agreementType: string; content: string; versions: Array<{ version: string; content: string; savedBy: string; savedByName: string; savedAt: string }> };
    deliveryInformation: { agreementType: string; content: string; versions: Array<{ version: string; content: string; savedBy: string; savedByName: string; savedAt: string }> };
    contractAgreement: { agreementType: string; content: string; versions: Array<{ version: string; content: string; savedBy: string; savedByName: string; savedAt: string }> };
  }> => {
    const response = await nestApi.get('/admin/agreement-info');
    return response.data;
  },

  updateAgreementInfo: async (data: {
    field: 'personAgreement' | 'companyAgreement' | 'privacyPolicy' | 'refundPolicy' | 'deliveryInformation' | 'contractAgreement';
    content: string;
    /** Terms of service only: name, pilot/commercial and change type of the new version. */
    title?: string;
    termsTrack?: TermsTrack;
    changeType?: Exclude<TermsChangeType, 'initial'>;
  }): Promise<void> => {
    await nestApi.put('/admin/agreement-info', data);
  },

  getUserTermsAcceptances: async (userId: string): Promise<TermsAcceptanceHistory> => {
    const response = await nestApi.get(`/admin/users/${encodeURIComponent(userId)}/terms-acceptances`);
    return response.data;
  },

  getTermsAcceptanceReport: async (filters: TermsReportFilters = {}): Promise<TermsAcceptanceReport> => {
    const response = await nestApi.get('/admin/reports/terms-acceptances', { params: termsReportParams(filters) });
    return response.data;
  },

  exportTermsAcceptanceReport: async (filters: TermsReportFilters = {}): Promise<{ blob: Blob; filename: string }> => {
    const response = await nestApi.get('/admin/reports/terms-acceptances/export.csv', {
      params: termsReportParams(filters),
      responseType: 'blob'
    });
    const ts = new Date().toISOString().slice(0, 10);
    return { blob: response.data, filename: `etappio-godkanda-villkor-${ts}.csv` };
  },

  getKpiDefaults: async (): Promise<{ enabledKpis: string[]; updatedAt: string; updatedBy: string }> => {
    const response = await nestApi.get('/admin/kpi-defaults');
    return {
      ...response.data,
      enabledKpis: response.data.enabledKpis ?? response.data.selectedKpis ?? []
    };
  },

  updateKpiDefaults: async (enabledKpis: string[]): Promise<any> => {
    const response = await nestApi.put('/admin/kpi-defaults', { enabledKpis, selectedKpis: enabledKpis });
    return response.data;
  },

  getRefundPolicySettings: async (): Promise<RefundPolicySettings> => {
    const response = await nestApi.get('/admin/refund-policy-settings');
    return response.data;
  },

  updateRefundPolicySettings: async (data: RefundPolicySettings): Promise<RefundPolicySettings> => {
    const response = await nestApi.put('/admin/refund-policy-settings', data);
    return response.data;
  },

  /** Konfiguration → Banker (ClickUp 869eu6fpm). */
  getBanks: async (): Promise<AdminBank[]> => {
    const response = await nestApi.get('/admin/banks');
    return response.data;
  },

  createBank: async (data: AdminBankInput & { name: string }): Promise<AdminBank> => {
    const response = await nestApi.post('/admin/banks', data);
    return response.data;
  },

  updateBank: async (id: string, data: AdminBankInput): Promise<AdminBank> => {
    const response = await nestApi.patch(`/admin/banks/${id}`, data);
    return response.data;
  },

  reorderBanks: async (bankIds: string[]): Promise<AdminBank[]> => {
    const response = await nestApi.put('/admin/banks/order', { bankIds });
    return response.data;
  },

  /** The bank guide's English (ClickUp 869eu8fd9). */
  publishEnglishBankGuidance: async (id: string): Promise<AdminBank> => {
    const response = await nestApi.post(`/admin/banks/${id}/guidance/en/publish`);
    return response.data;
  },

  getBankGuidancePreview: async (id: string, lang: 'sv' | 'en'): Promise<BankGuidancePreview> => {
    const response = await nestApi.get(`/admin/banks/${id}/guidance-preview`, { params: { lang } });
    return response.data;
  },

  getConflictSettings: async (): Promise<ConflictSettings> => {
    const response = await nestApi.get('/admin/conflict-settings');
    return response.data;
  },

  updateConflictSettings: async (data: ConflictSettingsUpdate): Promise<ConflictSettings> => {
    const response = await nestApi.put('/admin/conflict-settings', data);
    return response.data;
  },

  getDebugSettings: async (): Promise<DebugSettings> => {
    const response = await nestApi.get('/admin/debug-settings');
    return response.data;
  },

  getStagingTestUsers: async (q = ''): Promise<{ users: StagingTestUser[]; selectedUsers: StagingTestUser[] }> => {
    const response = await nestApi.get('/admin/staging-test-users', { params: { q } });
    return response.data;
  },

  updateStagingTestUsers: async (ids: string[]): Promise<DebugSettings> => {
    const response = await nestApi.put('/admin/debug-settings', { bankidStagingTestUserIds: ids });
    return response.data;
  },

  updateDebugSettings: async (data: {
    bankidStagingTestUserIds?: string[];
    emailVerificationEnabled: boolean;
    notificationEmailEnabled: boolean;
    bankidOnboardingProvider: 'simulated' | 'tic_test' | 'bankid_prod';
    bankidRequiredAssurance: 'simulated' | 'test' | 'production';
  }): Promise<DebugSettings> => {
    const response = await nestApi.put('/admin/debug-settings', data);
    return response.data;
  },

  getDebugDatabaseScenarios: async (): Promise<DebugDatabaseScenariosResponse> => {
    const response = await nestApi.get('/admin/debug-database-scenarios');
    return response.data;
  },

  resetDebugDatabaseScenario: async (id: string, confirmation: string): Promise<{ reset: true; scenarioId: string }> => {
    const response = await nestApi.post(`/admin/debug-database-scenarios/${id}/reset`, { confirmation });
    return response.data;
  },

  saveDebugDatabaseScenario: async (data: {
    name: string;
    description?: string;
    confirmation: string;
  }): Promise<DebugDatabaseScenario> => {
    const response = await nestApi.post('/admin/debug-database-scenarios', data);
    return response.data;
  },

  getAdyenRuntime: async (): Promise<AdyenRuntimeStatus> => {
    const response = await nestApi.get('/admin/adyen-runtime');
    return response.data;
  },

  getPaymentMethods: async (): Promise<AdminPaymentMethodsOverview> => {
    const response = await nestApi.get('/admin/payment-methods');
    return response.data;
  },

  updatePaymentMethod: async (
    method: DepositMethodKey,
    change: { enabled: boolean; reason: string }
  ): Promise<AdminPaymentMethodsOverview> => {
    const response = await nestApi.put(`/admin/payment-methods/${method}`, change);
    return response.data;
  },

  getPaymentProvider: async (): Promise<PaymentProviderSettings> => {
    const response = await nestApi.get('/admin/payment-provider');
    return response.data;
  },

  checkPingSandbox: async (): Promise<PingSandboxCheckResult> => {
    const response = await nestApi.post('/admin/ping-sandbox/check');
    return response.data;
  },

  updatePaymentProvider: async (data: Pick<PaymentProviderSettings, 'provider' | 'mode'> & { confirmation?: string }): Promise<PaymentProviderSettings> => {
    const response = await nestApi.put('/admin/payment-provider', data);
    return response.data;
  },

  updateAdyenRuntime: async (data: {
    requestedMode: 'mock' | 'sandbox';
    confirmation?: string;
  }): Promise<AdyenRuntimeStatus> => {
    const response = await nestApi.put('/admin/adyen-runtime', data);
    return response.data;
  },

  getAdyenWebhookConfigStatus: async (): Promise<AdyenWebhookConfigStatus> => {
    const response = await nestApi.get('/admin/adyen-webhook-config/status');
    return response.data;
  },

  applyAdyenWebhookConfig: async (confirmation: string): Promise<AdyenWebhookApplyResult> => {
    const response = await nestApi.post('/admin/adyen-webhook-config/apply', { confirmation });
    return response.data;
  },

  testAdyenWebhookConfig: async (confirmation: string): Promise<AdyenWebhookTestResult> => {
    const response = await nestApi.post('/admin/adyen-webhook-config/test', { confirmation });
    return response.data;
  },

  getAllAdmins: async (): Promise<AdminUser[]> => {
    const response = await nestApi.get('/admin/admins');
    return response.data;
  },

  createAdmin: async (data: { email: string; isSupport?: boolean }): Promise<AdminUser> => {
    const response = await nestApi.post('/admin/admins', data);
    return response.data;
  },

  updateAdmin: async (id: string, data: { phone?: string; address?: string; postalCode?: string; city?: string; isAdmin?: boolean; isSupport?: boolean }): Promise<AdminUser> => {
    const response = await nestApi.patch(`/admin/admins/${id}`, data);
    return response.data;
  },

};

// System APIs (for all authenticated users)
export const systemApi = {
  getChangelog: async (): Promise<ChangelogDocument> => {
    const response = await nestApi.get('/system/changelog');
    return response.data;
  },

  getSystemInfo: async (): Promise<{
    companyinfo: string;
    lawyerinfo: string;
    inspectorinfo: string;
    personinfo: string;
    admininfo: string;
    extraimportant: string;
    updatedAt: string;
    updatedBy: string;
  }> => {
    const response = await nestApi.get('/system/info');
    return normalizeSystemInfo(response.data);
  },

  getAgreement: async (agreementType: string): Promise<{
    agreementType: string;
    content: string;
    savedAt: string | null;
    version: string | null;
  }> => {
    const response = await nestApi.get(`/system/agreement/${agreementType}`);
    return response.data;
  },

  /** One terms version exactly as published (what a user accepted). */
  getTermsOfServiceVersion: async (version: string): Promise<TermsOfServiceDocument> => {
    const response = await nestApi.get(`/system/terms-of-service/versions/${encodeURIComponent(version)}`);
    return response.data;
  },

  getTermsOfService: async (): Promise<TermsOfServiceDocument> => {
    const response = await nestApi.get('/system/terms-of-service');
    return response.data;
  },

  getPrivacyPolicy: async (): Promise<PrivacyPolicyDocument> => {
    const response = await nestApi.get('/system/privacy-policy');
    return response.data;
  },

  getRefundPolicy: async (): Promise<RefundPolicyDocument> => {
    const response = await nestApi.get('/system/refund-policy');
    return response.data;
  },

  getDeliveryInformation: async (): Promise<DeliveryInformationDocument> => {
    const response = await nestApi.get('/system/delivery-information');
    return response.data;
  },

  getKpiDefaults: async (): Promise<{ enabledKpis: string[]; updatedAt: string; updatedBy: string }> => {
    const response = await nestApi.get('/system/kpi-defaults');
    return response.data;
  },

  /** Integration modes for the environment indicator (refused for end users in production). */
  getRuntimeContext: async (): Promise<RuntimeContext> => {
    const response = await nestApi.get('/system/runtime-context');
    return response.data;
  },

  getApiVersion: async (): Promise<{ version: string; commit: string; builtAt: string | null }> => {
    const response = await nestApi.get('/system/version');
    return response.data;
  }
};

export type RuntimeContext = {
  nodeEnv: string;
  bankid: {
    onboardingProvider: 'simulated' | 'tic_test' | 'bankid_prod';
    requiredAssurance: 'simulated' | 'test' | 'production';
  };
  payments: { provider: string; mode: string } | null;
  signing: { provider: string };
};

// User Preferences APIs (for all users)
export const userPreferencesApi = {
  getPreferences: async (): Promise<Record<string, any>> => {
    return getMergedPreferences();
  },

  savePreference: async (key: string, value: any): Promise<void> => {
    await saveMergedPreference(key, value);
  }
};

// Contract APIs
export type SigningRoomSession = {
  processId: string;
  projectId: string;
  contractId: string;
  signingUrl: string;
  callbackOrigin: string;
  startedAt: string;
  loadPolicy: { timeoutMs: number; p95Ms: number | null; sampleCount: number };
};

export type SigningRoomActivity = {
  processId: string;
  observedAt: string;
  document: { state: 'unknown' | 'loaded' | 'failed'; eventId: string | null; occurredAt: string | null };
  signingStarted: boolean;
  activityObserved?: boolean;
  reloadAllowed?: boolean;
  contract: Contract;
};

export const contractApi = {
  prepareSigningRoom: async (id: string, processId: string): Promise<SigningRoomSession> => {
    const { data } = await nestApi.post<SigningRoomSession>(`/contracts/${encodeURIComponent(id)}/signing/room`, { processId }, { timeout: 30_000 });
    return data;
  },

  signingRoomActivity: async (id: string, processId: string, since: string, signal?: AbortSignal, verifyProvider = false): Promise<SigningRoomActivity> => {
    const { data } = await nestApi.get<SigningRoomActivity>(`/contracts/${encodeURIComponent(id)}/signing/activity`, {
      params: { processId, since, ...(verifyProvider ? { verifyProvider: 'true' } : {}) },
      signal, timeout: verifyProvider ? 15_000 : 4000,
    });
    return { ...data, contract: normalizeContract(data.contract) };
  },
  /**
   * The signed contract as stored from the signing provider (with its
   * signatures) – for the contract's parties and admin. 404 until both signed.
   */
  getSignedDocument: async (contractId: string): Promise<DocumentRecord> => {
    const response = await nestApi.get(`/contracts/${encodeURIComponent(contractId)}/signed-document`);
    return normalizeDocument(response.data);
  },

  getSigningProvider: async (): Promise<{ provider: string }> => {
    const response = await nestApi.get('/signing/provider');
    return response.data;
  },

  getAllContracts: async (): Promise<Contract[]> => {
    const response = await nestApi.get('/contracts', { params: { limit: 500 } });
    const contracts: Contract[] = response.data.map(normalizeContract);
    rememberAgreementNumbers(contracts);
    return contracts;
  },

  getContracts: async (projectId: string): Promise<Contract[]> => {
    const response = await nestApi.get(`/projects/${projectId}/contracts`);
    const contracts: Contract[] = response.data.map(normalizeContract);
    rememberAgreementNumbers(contracts);
    return contracts;
  },

  getContract: async (id: string): Promise<Contract> => {
    const response = await nestApi.get(`/contracts/${id}`);
    return normalizeContract(response.data);
  },

  getReplacement: async (id: string): Promise<ContractReplacement | null> => {
    const response = await nestApi.get(`/contracts/${id}/replacement`);
    return response.data ? normalizeContractReplacement(response.data) : null;
  },

  finalizeReplacement: async (id: string): Promise<ContractReplacement | null> => {
    const response = await nestApi.post(`/contracts/${id}/replacement/finalize`);
    return response.data ? normalizeContractReplacement(response.data) : null;
  },

  respondToReplacementProposal: async (
    id: string,
    data: { action: 'approve' | 'reject'; comment?: string }
  ): Promise<ContractReplacement> => {
    const response = await nestApi.post(`/contracts/${id}/replacement/respond`, data);
    return normalizeContractReplacement(response.data);
  },

  withdrawReplacementProposal: async (
    id: string,
    reason?: string
  ): Promise<ContractReplacement> => {
    const response = await nestApi.post(`/contracts/${id}/replacement/withdraw`, { reason });
    return normalizeContractReplacement(response.data);
  },

  getNextContractNumber: async (projectId: string): Promise<{ nextContractNumber: number }> => {
    const response = await nestApi.get(`/projects/${projectId}/contracts/next-number`);
    return response.data;
  },

  getNegativeVariationRefundCapacity: async (projectId: string, contractId?: string): Promise<{
    projectId: string;
    availableAmountOre: number;
    currency: string;
  }> => {
    const response = await nestApi.get(
      `/projects/${projectId}/contracts/negative-variation-refund-capacity`, {params:{contractId}}
    );
    return response.data;
  },

  createContract: async (projectId: string, data: CreateContractData): Promise<Contract> => {
    const response = await nestApi.post(`/projects/${projectId}/contracts`, toNestContractPayload(data));
    return normalizeContract(response.data);
  },

  updateContract: async (id: string, data: Partial<CreateContractData>): Promise<Contract> => {
    const response = await nestApi.patch(`/contracts/${id}`, toNestContractPayload(data));
    return normalizeContract(response.data);
  },

  deleteContract: async (id: string): Promise<void> => {
    await nestApi.delete(`/contracts/${id}`);
  },

  getContractDescriptions: async (contractId: string): Promise<{
    variationReason?: { content: string };
    variationOther?: { content: string };
    contractName: { id: string; contractId: string; projectId: string; field: string; content: string; versions: Array<{ version: string; content: string; savedBy: string; savedByName: string; savedAt: string }> };
    assignmentDescription: { id: string; contractId: string; projectId: string; field: string; content: string; versions: Array<{ version: string; content: string; savedBy: string; savedByName: string; savedAt: string }> };
    other: { id: string; contractId: string; projectId: string; field: string; content: string; versions: Array<{ version: string; content: string; savedBy: string; savedByName: string; savedAt: string }> };
    referToAttachments: { id: string; contractId: string; projectId: string; field: string; content: string; versions: Array<{ version: string; content: string; savedBy: string; savedByName: string; savedAt: string }> };
  }> => {
    const response = await nestApi.get(`/contracts/${contractId}/descriptions`);
    return response.data;
  },

  // Returns the stored field, including its version history, so autosave can
  // confirm what the server actually saved.
  changeVariationMode: async (contractId: string, mode: 'milestones' | 'price_adjustment'): Promise<{ contract: Contract; replacement: ContractReplacement | null }> => {
    const response = await nestApi.put(`/contracts/${contractId}/variation-mode`, { mode });
    return { contract: normalizeContract(response.data.contract), replacement: response.data.replacement ? normalizeContractReplacement(response.data.replacement) : null };
  },

  updateContractDescription: async (contractId: string, data: { field: 'contractName' | 'assignmentDescription' | 'other' | 'referToAttachments' | 'variationReason' | 'variationOther'; content: string }): Promise<{
    content: string;
    versions: Array<{ version: string; content: string; savedBy: string; savedByName: string; savedAt: string }>;
  }> => {
    const response = await nestApi.put(`/contracts/${contractId}/descriptions`, data);
    return {
      content: response.data?.content ?? data.content,
      versions: response.data?.versions ?? []
    };
  },

  sendDraft: async (id: string, comment?: string): Promise<Contract> => {
    const response = await nestApi.post(`/contracts/${id}/send`, { comment });
    return normalizeContract(response.data);
  },

  respondToDraft: async (id: string, data: { response: 'draft_approved' | 'draft_rejected'; comment?: string }): Promise<Contract> => {
    const response = await nestApi.post(`/contracts/${id}/respond`, {
      action: data.response === 'draft_approved' ? 'approve' : 'reject',
      comment: data.comment
    });
    return normalizeContract(response.data);
  },

  getConflictVariationScope: async (id: string): Promise<{ milestoneId: string; milestoneNumber: number; contractId: string; contractNumber: number } | null> => {
    const response = await nestApi.get(`/contracts/${id}/conflict-variation-scope`);
    return response.data?.milestoneId ? response.data : null;
  },

  getCounterproposalReference: async (id: string): Promise<MilestoneMessage | null> => {
    const response = await nestApi.get(`/contracts/${id}/counterproposal-reference`);
    return response.data?.id ? response.data : null;
  },

  submitCounterproposal: async (id: string, data: {
    message: string; requestId: string; confirmRejection: true;
    expectedSigningProcessId: string | null; expectedSigningSentAt: string | null;
  }): Promise<Contract> => {
    const response = await nestApi.post(`/contracts/${id}/counterproposal`, data);
    return normalizeContract(response.data);
  },

  getDraftMessages: async (id: string): Promise<any[]> => {
    const response = await nestApi.get(`/contracts/${id}/messages`);
    return response.data;
  },

  getHistory: async (id: string): Promise<ContractHistoryEntry[]> => {
    const response = await nestApi.get(`/contracts/${id}/history`);
    return response.data;
  },

  postDraftMessage: async (id: string, message: string, files: File[] = []): Promise<any[]> => {
    const response = await postMessageBody<any[]>(`/contracts/${id}/messages`, { message }, files);
    return response.data;
  },

  signContract: async (id: string, comment?: string): Promise<Contract> => {
    const response = await nestApi.post(`/contracts/${id}/sign`, { comment });
    return normalizeContract(response.data.contract ?? response.data);
  },

  initiateSigning: async (
    id: string,
    commentToCustomer?: string,
    milestoneStartDecisions?: MilestoneStartDecision[]
  ): Promise<{
    provider: string;
    externalDocumentId: string;
    externalSigningProcessId: string;
    signingUrl: string;
    status: string;
    contract: Contract;
  }> => {
    const trimmed = commentToCustomer?.trim();
    const response = await nestApi.post(
      `/contracts/${id}/signing/initiate`,
      {
        ...(trimmed ? { commentToCustomer: trimmed } : {}),
        ...(milestoneStartDecisions !== undefined ? { milestoneStartDecisions } : {})
      }
    );
    return {
      ...response.data,
      contract: normalizeContract(response.data.contract)
    };
  },

  // Withdraws a contract that is out for signing (not yet signed by the
  // customer) so it can be changed: the agreement is cancelled at the signing
  // provider and the contract becomes an unsigned draft again.
  /**
   * `beforeCompanySignature`: only stop a signing the company has not signed
   * (refused with SIGNING_ALREADY_SENT once it has, see ClickUp 869fnf0qc).
   */
  withdrawSigning: async (id: string, reason?: string, options: { beforeCompanySignature?: boolean } = {}): Promise<{
    contract: Contract;
    providerOutcome: 'cancelled' | 'already_cancelled';
  }> => {
    const trimmed = reason?.trim();
    const response = await nestApi.post(`/contracts/${id}/signing/withdraw`, {
      ...(trimmed ? { reason: trimmed } : {}),
      ...(options.beforeCompanySignature ? { beforeCompanySignature: true } : {}),
    });
    return {
      ...response.data,
      contract: normalizeContract(response.data.contract)
    };
  },

  signingLink: async (
    id: string,
    milestoneStartDecisions?: MilestoneStartDecision[]
  ): Promise<{
    provider: string;
    externalDocumentId: string | null;
    externalSigningProcessId: string;
    signingUrl: string;
    status: string;
    contract: Contract;
  }> => {
    const response = milestoneStartDecisions === undefined
      ? await nestApi.post(`/contracts/${id}/signing/link`)
      : await nestApi.post(`/contracts/${id}/signing/link`, { milestoneStartDecisions });
    return {
      ...response.data,
      contract: normalizeContract(response.data.contract)
    };
  },

  syncSigning: async (id: string, timeoutMs?: number): Promise<{
    provider: string;
    externalDocumentId: string | null;
    externalSigningProcessId: string;
    status: string;
    contract: Contract;
  }> => {
    const response = timeoutMs === undefined
      ? await nestApi.post(`/contracts/${id}/signing/sync`)
      : await nestApi.post(`/contracts/${id}/signing/sync`, {}, { timeout: timeoutMs });
    return {
      ...response.data,
      contract: normalizeContract(response.data.contract)
    };
  },

  adminToggleLock: async (id: string): Promise<Contract> => {
    const response = await nestApi.post(`/contracts/${id}/admin-toggle-lock`);
    return normalizeContract(response.data);
  },

  getPendingReview: async (): Promise<Array<{
    projectId: string;
    projectNumber: number;
    contractId: string;
    contractNumber: number;
    /** Agreement name; null while the contract has none (869fnhht3). */
    contractName: string | null;
    contractAmount: string;
    draftStatus: string;
    notices?: Array<{ id: string; type: "comment" | "rejection"; message: string; authorName: string | null; byRole: string | null; createdAt: string }>;
  }>> => {
    const response = await nestApi.get('/contracts/pending-review');
    return response.data;
  },

  getAdminPendingReview: async (): Promise<Array<{
    projectId: string;
    projectNumber: number;
    contractId: string;
    contractNumber: number;
    /** Agreement name; null while the contract has none (869fnhht3). */
    contractName: string | null;
    contractAmount: string;
    draftStatus: string;
    companyNumber: number;
    companyName: string;
    customerName: string;
    customerNumber: number;
  }>> => {
    const response = await nestApi.get('/contracts/admin/pending-review');
    return response.data;
  }
};

// Milestone APIs
export const milestoneApi = {
  createStageVariation: async (milestoneId: string, data: {
    reason: string;
    selectedMilestoneIds: string[];
    referenceMessageId?: string;
  }): Promise<ContractReplacement> => {
    const response = await nestApi.post(`/milestones/${milestoneId}/variation-plan`, data);
    return normalizeContractReplacement(response.data);
  },

  createContractStageVariation: async (contractId: string, data: {
    reason: string; selectedMilestoneIds: string[]; entryMilestoneId?: string;
    referenceMessageId?: string; newStageRequested?: boolean;
  }): Promise<ContractReplacement> => {
    const response = await nestApi.post(`/contracts/${contractId}/variation-plan`, data);
    return normalizeContractReplacement(response.data);
  },

  updateStageVariationPlan: async (contractId: string, data: {
    grossDeltaOre: number;
    selectedMilestoneIds?: string[];
    milestones: Array<{ id?: string; sourceMilestoneId?: string; restoresMilestoneId?: string; description: string; grossAmountOre?: number; amountOre: number; endDate?: string | null }>;
  }): Promise<ContractReplacement> => {
    const response = await nestApi.put(`/contracts/${contractId}/variation-plan`, data);
    return normalizeContractReplacement(response.data);
  },
  getMilestones: async (contractId: string): Promise<Milestone[]> => {
    const response = await nestApi.get(`/contracts/${contractId}/milestones`);
    return response.data.map(normalizeMilestone);
  },

  createReplacementContract: async (
    milestoneId: string,
    data: {
      reason: string;
      referenceMessageId?: string;
      sellerFeeTreatment?: 'retain' | 'refund';
      sellerFeeFundingSource?: 'etappio_balance' | 'seller_balance';
    }
  ): Promise<ContractReplacement> => {
    const response = await nestApi.post(`/milestones/${milestoneId}/replacement-contract`, data);
    return normalizeContractReplacement(response.data);
  },

  getNextMilestoneNumber: async (contractId: string): Promise<{ nextMilestoneNumber: number }> => {
    const response = await nestApi.get(`/contracts/${contractId}/milestones/next-number`);
    return response.data;
  },

  createMilestone: async (contractId: string, data: CreateMilestoneData): Promise<Milestone> => {
    const response = await nestApi.post(`/contracts/${contractId}/milestones`, toNestMilestonePayload(data));
    return normalizeMilestone(response.data);
  },

  replaceMilestonePlan: async (contractId: string, data: {
    pricingMode: 'top_down' | 'bottom_up';
    contractAmount: string;
    afterRotAmount: string;
    milestones: Array<CreateMilestoneData & { id?: string }>;
  }): Promise<Contract> => {
    const response = await nestApi.put(`/contracts/${contractId}/milestone-plan`, {
      pricingMode: data.pricingMode,
      contractAmountOre: parseSekToOre(data.contractAmount),
      afterRotAmountOre: parseSekToOre(data.afterRotAmount),
      milestones: data.milestones.map(milestone => ({ ...toNestMilestonePayload(milestone), ...(milestone.id ? { id: milestone.id } : {}), endDate: milestone.endDate || null }))
    });
    return normalizeContract(response.data);
  },

  replaceReplacementMilestonePlan: async (
    contractId: string,
    data: {
      pricingMode?: 'top_down' | 'bottom_up';
      contractAmount: string;
      afterRotAmount: string;
      milestones: Array<CreateMilestoneData & { id?: string }>;
    }
  ): Promise<ContractReplacement> => {
    const contractAmountOre = parseSekToOre(data.contractAmount);
    const afterRotAmountOre = parseSekToOre(data.afterRotAmount);
    const response = await nestApi.put(`/contracts/${contractId}/replacement/milestone-plan`, {
      ...(data.pricingMode ? { pricingMode: data.pricingMode } : {}),
      contractAmountOre,
      afterRotAmountOre,
      milestones: data.milestones.map(milestone => ({
        ...toNestMilestonePayload(milestone),
        ...(milestone.id ? { id: milestone.id } : {}),
        endDate: milestone.endDate || null
      }))
    });
    return normalizeContractReplacement(response.data);
  },

  updateMilestone: async (id: string, data: Partial<CreateMilestoneData>): Promise<Milestone> => {
    const response = await nestApi.patch(`/milestones/${id}`, {
      ...toNestMilestonePayload(data),
      ...(data.endDate !== undefined ? { endDate: data.endDate || null } : {})
    });
    return normalizeMilestone(response.data);
  },

  deleteMilestone: async (id: string): Promise<void> => {
    await nestApi.delete(`/milestones/${id}`);
  },

  progressMilestone: async (
    id: string,
    action: 'start' | 'submit_for_attestation' | 'start_dispute_resolution' | 'resubmit_for_attestation',
    comment?: string
  ): Promise<Milestone> => {
    const response = await nestApi.post(`/milestones/${id}/progress`, {
      action,
      ...(comment?.trim() ? { comment: comment.trim() } : {})
    });
    return normalizeMilestone(response.data);
  },

  updateStarts: async (
    contractId: string,
    decisions: MilestoneStartDecision[],
    context: 'manual' | 'attestation' = 'manual',
    acceptFundingRisk?: boolean
  ): Promise<Milestone[]> => {
    const response = await nestApi.post(`/contracts/${contractId}/milestone-starts`, {
      context,
      decisions,
      ...(acceptFundingRisk ? { acceptFundingRisk } : {})
    });
    return response.data.map(normalizeMilestone);
  },

  startAttestationBankId: async (id: string): Promise<MilestoneBankIdSessionResponse> => {
    const response = await nestApi.post(`/milestones/${id}/attestation-bankid/start`);
    return response.data;
  },

  getAttestationBankIdStatus: async (
    id: string,
    verificationRef: string
  ): Promise<MilestoneBankIdSessionResponse> => {
    const response = await nestApi.get(
      `/milestones/${id}/attestation-bankid/${verificationRef}`
    );
    return response.data;
  },

  collectAttestationBankId: async (
    id: string,
    verificationRef: string
  ): Promise<MilestoneBankIdSessionResponse> => {
    const response = await nestApi.post(
      `/milestones/${id}/attestation-bankid/${verificationRef}/collect`
    );
    return response.data;
  },

  cancelAttestationBankId: async (id: string, verificationRef: string): Promise<void> => {
    await nestApi.post(`/milestones/${id}/attestation-bankid/${verificationRef}/cancel`);
  },

  attestMilestone: async (id: string, data: {
    action: 'approved' | 'rejected';
    bankidVerificationRef?: string;
    message?: string;
    milestoneStartDecisions?: MilestoneStartDecision[];
    /** Files with the approval or the objection (ClickUp 869fj6n1d, 869fmfeah). */
    files?: File[];
  }): Promise<any> => {
    if (data.files && data.files.length > 0) {
      // Bracket keys are parsed into nested objects by the API's multipart parser.
      const decisions = Object.fromEntries((data.milestoneStartDecisions ?? []).flatMap((decision, index) => [
        [`milestoneStartDecisions[${index}][milestoneId]`, decision.milestoneId],
        [`milestoneStartDecisions[${index}][started]`, decision.started]
      ]));
      const response = await postMessageBody(`/milestones/${id}/attest`, {
        action: data.action,
        bankidVerificationRef: data.bankidVerificationRef,
        comment: data.message,
        ...decisions
      }, data.files, { timeout: ATTESTATION_TIMEOUT_MS });
      return response.data;
    }
    const response = await nestApi.post(`/milestones/${id}/attest`, {
      action: data.action,
      ...(data.bankidVerificationRef
        ? { bankidVerificationRef: data.bankidVerificationRef }
        : {}),
      comment: data.message,
      ...(data.milestoneStartDecisions !== undefined
        ? { milestoneStartDecisions: data.milestoneStartDecisions }
        : {})
    }, { timeout: ATTESTATION_TIMEOUT_MS });
    return response.data;
  },

  startConflict: async (id: string, comment?: string): Promise<any> => {
    const response = await nestApi.post(`/milestones/${id}/conflict`, {
      ...(comment?.trim() ? { comment: comment.trim() } : {})
    });
    return response.data;
  },

  startDisputeBankId: async (id: string): Promise<MilestoneBankIdSessionResponse> => {
    const response = await nestApi.post(`/milestones/${id}/dispute-bankid/start`);
    return response.data;
  },

  getDisputeBankIdStatus: async (
    id: string,
    verificationRef: string
  ): Promise<MilestoneBankIdSessionResponse> => {
    const response = await nestApi.get(
      `/milestones/${id}/dispute-bankid/${verificationRef}`
    );
    return response.data;
  },

  collectDisputeBankId: async (
    id: string,
    verificationRef: string
  ): Promise<MilestoneBankIdSessionResponse> => {
    const response = await nestApi.post(
      `/milestones/${id}/dispute-bankid/${verificationRef}/collect`
    );
    return response.data;
  },

  cancelDisputeBankId: async (id: string, verificationRef: string): Promise<void> => {
    await nestApi.post(`/milestones/${id}/dispute-bankid/${verificationRef}/cancel`);
  },

  startDispute: async (
    id: string,
    bankidVerificationRef: string,
    comment?: string
  ): Promise<any> => {
    const response = await nestApi.post(`/milestones/${id}/dispute`, {
      bankidVerificationRef,
      ...(comment?.trim() ? { comment: comment.trim() } : {})
    });
    return response.data;
  },

  downloadDisputeExport: async (id: string): Promise<void> => {
    const response = await nestApi.get(`/milestones/${id}/dispute-export`, {
      responseType: 'blob'
    });
    const disposition = String(response.headers['content-disposition'] ?? '');
    const filename = disposition.match(/filename="?([^";]+)"?/i)?.[1]
      ?? `etappio-tvist-${id}.pdf`;
    const url = URL.createObjectURL(response.data);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  },

  getMessages: async (milestoneId: string): Promise<MilestoneMessage[]> => {
    const response = await nestApi.get<MilestoneMessage[]>(`/milestones/${milestoneId}/messages`);
    return response.data;
  },

  postMessage: async (
    milestoneId: string,
    message: string,
    options: { context?: 'attestation_question' | 'attestation_request'; files?: File[] } = {}
  ): Promise<MilestoneMessage> => {
    const response = await postMessageBody<MilestoneMessage>(`/milestones/${milestoneId}/messages`,
      { message, context: options.context }, options.files);
    return response.data;
  }
};

/** Milestone conversations across the user's projects (ClickUp 869fegbvk, 869feg849). */
export const messageThreadApi = {
  list: async (projectId?: string): Promise<MessageThread[]> => {
    const response = await nestApi.get<MessageThread[]>('/me/message-threads', { params: projectId ? { projectId } : undefined });
    return response.data;
  },
  summary: async (): Promise<MessageThreadSummary> => {
    const response = await nestApi.get<MessageThreadSummary>('/me/message-threads/summary');
    return response.data;
  },
  markRead: async (threadId: string, threadType: 'milestone' | 'contract' | 'project' = 'milestone'): Promise<void> => {
    await nestApi.post(`/me/message-threads/${threadType}/${threadId}/read`);
  },
  /** Every message with one counterpart, oldest first. */
  conversation: async (counterpartKey: string): Promise<ConversationMessage[]> => {
    const response = await nestApi.get<ConversationMessage[]>(`/me/message-threads/conversations/${encodeURIComponent(counterpartKey)}/messages`);
    return response.data;
  },
  readConversation: async (counterpartKey: string): Promise<void> => {
    await nestApi.post(`/me/message-threads/conversations/${encodeURIComponent(counterpartKey)}/read`);
  },
  /**
   * Downloads a project's messages (ClickUp 869fjkmyw): the whole project or
   * one subject, as a PDF, or a ZIP with the attached files too.
   */
  exportProjectMessages: async (
    projectId: string,
    options: { thread?: { threadType: 'milestone' | 'contract' | 'project'; threadId: string }; attachments: boolean }
  ): Promise<void> => {
    const response = await nestApi.get(`/projects/${projectId}/messages/export`, {
      params: {
        scope: options.thread ? 'thread' : 'project',
        ...(options.thread ? { threadType: options.thread.threadType, threadId: options.thread.threadId } : {}),
        attachments: options.attachments ? 'true' : 'false',
      },
      responseType: 'blob',
    });
    const disposition = String(response.headers['content-disposition'] ?? '');
    const filename = disposition.match(/filename="?([^";]+)"?/i)?.[1]
      ?? `etappio-meddelanden.${options.attachments ? 'zip' : 'pdf'}`;
    const url = URL.createObjectURL(response.data);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  },
  /** A message about the project in general ("Allmänt om projektet"). */
  postToProject: async (projectId: string, message: string, files: File[] = []): Promise<MilestoneMessage> => {
    const response = await postMessageBody<MilestoneMessage>(`/projects/${projectId}/messages`, { message }, files);
    return response.data;
  }
};

export const inactivityApi = {
  listForProject: async (projectId: string): Promise<import('../types').InactivityCase[]> => {
    const response = await nestApi.get(`/projects/${projectId}/inactivity-cases`);
    return response.data;
  },

  downloadFinalReport: async (caseId: string): Promise<void> => {
    const response = await nestApi.get(`/inactivity-cases/${caseId}/final-report`, {
      responseType: 'blob'
    });
    const disposition = String(response.headers['content-disposition'] ?? '');
    const filename = disposition.match(/filename="?([^";]+)"?/i)?.[1]
      ?? `etappio-slutrapport-${caseId}.pdf`;
    const url = URL.createObjectURL(response.data);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  },

  startResolutionBankId: async (caseId: string): Promise<MilestoneBankIdSessionResponse> => {
    const response = await nestApi.post(`/inactivity-cases/${caseId}/resolution-bankid/start`);
    return response.data;
  },

  getResolutionBankIdStatus: async (
    caseId: string,
    verificationRef: string
  ): Promise<MilestoneBankIdSessionResponse> => {
    const response = await nestApi.get(
      `/inactivity-cases/${caseId}/resolution-bankid/${verificationRef}`
    );
    return response.data;
  },

  collectResolutionBankId: async (
    caseId: string,
    verificationRef: string
  ): Promise<MilestoneBankIdSessionResponse> => {
    const response = await nestApi.post(
      `/inactivity-cases/${caseId}/resolution-bankid/${verificationRef}/collect`
    );
    return response.data;
  },

  cancelResolutionBankId: async (caseId: string, verificationRef: string): Promise<void> => {
    await nestApi.post(
      `/inactivity-cases/${caseId}/resolution-bankid/${verificationRef}/cancel`
    );
  },

  resolve: async (
    caseId: string,
    bankidVerificationRef: string
  ): Promise<import('../types').InactivityCase> => {
    const response = await nestApi.post(`/inactivity-cases/${caseId}/resolve`, {
      bankidVerificationRef
    });
    return response.data;
  }
};

// Contact APIs
export interface ContactMessageData {
  name: string;
  email: string;
  phone: string;
  message: string;
}

export interface ContactMessageResponse {
  message: string;
  data: {
    id: string;
    name: string;
    email: string;
    phone: string;
    message: string;
    createdAt: string;
    emailDeliveryStatus?: 'sent' | 'failed' | 'skipped';
  };
}

export const contactApi = {
  sendSupportMessage: async (data: { message: string; context: import('../utils/supportContext').SupportContext }): Promise<ContactMessageResponse> => {
    const response = await nestApi.post('/contact/support', data);
    return response.data;
  },
  sendMessage: async (data: ContactMessageData): Promise<ContactMessageResponse> => {
    const response = await nestApi.post('/contact', data);
    return response.data;
  }
};

/** Posts a message, as multipart when files are sent with it (ClickUp
 * 869fj6n1d): text fields plus the files in `files`; plain JSON otherwise. The
 * multipart header is set explicitly, or axios turns the form into JSON under
 * the client's default JSON content type. */
const postMessageBody = <T,>(url: string, fields: Record<string, string | boolean | undefined>, files: File[] = [], config: { timeout?: number } = {}) => {
  if (files.length === 0) {
    const body = Object.fromEntries(Object.entries(fields).filter(([, value]) => value !== undefined));
    return Object.keys(config).length > 0 ? nestApi.post<T>(url, body, config) : nestApi.post<T>(url, body);
  }
  const form = new FormData();
  Object.entries(fields).forEach(([key, value]) => { if (value !== undefined) form.append(key, String(value)); });
  files.forEach(file => form.append('files', file));
  return nestApi.post<T>(url, form, { ...config, headers: { 'Content-Type': 'multipart/form-data' } });
};

// Document APIs
export const documentApi = {
  getFolderTree: async (projectId: string): Promise<FolderTree> => {
    const [project, contracts, folders, documents] = await Promise.all([
      projectApi.getProject(projectId),
      contractApi.getContracts(projectId),
      nestApi.get(`/dms/folders/${projectId}`),
      nestApi.get('/documents', { params: { projectId, limit: 100 } })
    ]);
    const allDocuments = (documents.data as DocumentRecord[]).map((document) =>
      normalizeDocument(document, contracts, project)
    );

    return {
      projectId,
      projectNumber: project.projectNumber,
      companyNumber: project.companyNumber,
      projectFileCount: allDocuments.filter((document) => !document.contractNumber && !document.folderId).length,
      totalFileCount: allDocuments.length,
      contracts: contracts.map((contract) => ({
        contractNumber: contract.contractNumber,
        contractName: contractTitle(contract) || null,
        // Message attachments about the contract or its stages count too (869fj6n1d).
        fileCount: allDocuments.filter((document) => document.contractNumber === contract.contractNumber
          || document.source?.contractNumber === contract.contractNumber).length
      })),
      folders: folders.data.map((folder: DmsFolder) => ({
        ...folder,
        createdBy: folder.createdBy ?? '',
        createdByName: folder.createdByName ?? '',
        fileCount: allDocuments.filter((document) => document.folderId === folder.id).length
      }))
    };
  },

  listDocuments: async (projectId: string, contractNumber?: number | null, folderId?: string | null): Promise<DocumentRecord[]> => {
    const params: Record<string, string | number> = { projectId, limit: 100 };
    const contracts = await contractApi.getContracts(projectId);
    if (folderId) {
      params.folderId = folderId;
    } else if (contractNumber !== undefined && contractNumber !== null) {
      const contractId = contracts.find((contract) => contract.contractNumber === contractNumber)?.id;
      if (contractId) params.contractId = contractId;
    }
    const response = await nestApi.get('/documents', { params });
    return response.data.map((document: DocumentRecord) => normalizeDocument(document, contracts));
  },

  getDocumentDetails: async (documentId: string): Promise<DocumentIntegrityDetails> => {
    const response = await nestApi.get(`/documents/${documentId}`);
    return normalizeDocument(response.data) as DocumentIntegrityDetails;
  },

  uploadFiles: async (projectId: string, files: File[], contractNumber?: number | null, folderId?: string | null): Promise<DocumentRecord[]> => {
    const contractId = await resolveContractId(projectId, contractNumber);
    const uploaded = await Promise.all(files.map(async (file) => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('projectId', projectId);
      if (contractId) formData.append('contractId', contractId);
      if (folderId) formData.append('folderId', folderId);

      const response = await nestApi.post('/documents', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return normalizeDocument(response.data);
    }));
    return uploaded;
  },

  downloadFile: async (documentId: string, fileName: string): Promise<void> => {
    const { url: downloadUrl } = await documentDownloadDescriptor(documentId);
    if (isAbsoluteUrl(downloadUrl)) {
      triggerBrowserDownload(downloadUrl, fileName);
      return;
    }
    const localPath = downloadUrl.startsWith('/v1')
      ? downloadUrl.substring('/v1'.length)
      : downloadUrl;
    const response = await nestApi.get(localPath, { responseType: 'blob' });
    const blob = new Blob([response.data]);
    const url = window.URL.createObjectURL(blob);
    triggerBrowserDownload(url, fileName);
    window.URL.revokeObjectURL(url);
  },

  downloadZip: async (documentIds: string[]): Promise<void> => {
    const response = await nestApi.post('/documents/download-zip', { documentIds }, {
      responseType: 'blob'
    });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'documents.zip');
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },

  getFileBlob: async (documentId: string): Promise<Blob> => {
    return fetchDocumentBlob(documentId);
  },

  renameDocument: async (documentId: string, newName: string): Promise<DocumentRecord> => {
    const response = await nestApi.put(`/documents/${documentId}/rename`, { newName });
    return normalizeDocument(response.data);
  },

  deleteDocument: async (documentId: string): Promise<void> => {
    await nestApi.delete(`/documents/${documentId}`);
  },

  linkToContract: async (documentId: string, contractNumber: number): Promise<void> => {
    await nestApi.put(`/documents/${documentId}/link-contract`, { contractNumber });
  },

  unlinkFromContract: async (documentId: string): Promise<void> => {
    await nestApi.put(`/documents/${documentId}/unlink-contract`);
  },

  createFolder: async (projectId: string, name: string): Promise<DmsFolder> => {
    const response = await nestApi.post('/dms/folders', { projectId, name });
    return {
      ...response.data,
      createdBy: response.data.createdBy ?? '',
      createdByName: response.data.createdByName ?? '',
      fileCount: response.data.fileCount ?? 0
    };
  },

  renameFolder: async (folderId: string, name: string): Promise<DmsFolder> => {
    const response = await nestApi.put(`/dms/folders/${folderId}`, { name });
    return {
      ...response.data,
      createdBy: response.data.createdBy ?? '',
      createdByName: response.data.createdByName ?? '',
      fileCount: response.data.fileCount ?? 0
    };
  },

  deleteFolder: async (folderId: string): Promise<void> => {
    await nestApi.delete(`/dms/folders/${folderId}`);
  },

  getAdminLibraryTree: async (): Promise<AdminLibraryCompany[]> => {
    const response = await nestApi.get('/documents/admin/library-tree');
    return response.data;
  }
};

// Notification APIs
export const notificationApi = {
  getMyNotifications: async (params?: { limit?: number; cursor?: string }): Promise<any[]> => {
    // Server supports keyset pagination: `limit` + `cursor` (createdAt of the
    // last item already seen), newest first. Omitting params preserves the old
    // behaviour for existing callers (bell icon).
    const response = await nestApi.get('/notifications', { params });
    return response.data;
  },

  getUnreadCount: async (): Promise<{ count: number }> => {
    const response = await nestApi.get('/notifications/unread-count');
    return response.data;
  },

  markAsRead: async (id: string): Promise<void> => {
    await nestApi.patch(`/notifications/${id}`, { read: true });
  },

  markAllAsRead: async (): Promise<void> => {
    await nestApi.post('/notifications/mark-all-read');
  },

  getAllNotifications: async (): Promise<any[]> => {
    const response = await nestApi.get('/notifications');
    return response.data;
  },

  getDefinitions: async (): Promise<any[]> => {
    const response = await nestApi.get('/admin/notification-definitions');
    return response.data;
  },

  createDefinition: async (data: any): Promise<any> => {
    const response = await nestApi.post('/admin/notification-definitions', data);
    return response.data;
  },

  updateDefinition: async (id: string, data: any): Promise<any> => {
    const response = await nestApi.patch(`/admin/notification-definitions/${id}`, data);
    return response.data;
  },

  // The user's own settings per category (ClickUp 869fkva3u).
  getPreferences: async (): Promise<NotificationPreference[]> => {
    const response = await nestApi.get('/notification-preferences');
    return response.data;
  },

  updatePreference: async (category: string, email: boolean): Promise<NotificationPreference[]> => {
    const response = await nestApi.put(`/notification-preferences/${encodeURIComponent(category)}`, { email });
    return response.data;
  },

  getDeliveries: async (params: { email?: string; limit?: number }): Promise<NotificationDelivery[]> => {
    const response = await nestApi.get('/admin/notification-deliveries', { params });
    return response.data;
  },

  // The e-mail templates rendered with their fixtures (ClickUp 869fkva4j).
  getTemplates: async (): Promise<NotificationTemplateSummary[]> => {
    const response = await nestApi.get('/admin/notification-templates');
    return response.data;
  },

  previewTemplate: async (eventKey: string, lang: 'sv' | 'en'): Promise<NotificationTemplatePreview> => {
    const response = await nestApi.get(
      `/admin/notification-templates/${encodeURIComponent(eventKey)}/preview`,
      { params: { lang } }
    );
    return response.data;
  }
};

export interface NotificationTemplateSummary {
  eventKey: string;
  category: string;
}

export interface NotificationTemplatePreview extends NotificationTemplateSummary {
  language: 'sv' | 'en';
  subject: string;
  html: string;
  text: string;
}

export interface NotificationPreference {
  category: string;
  /** Account, payments and deadlines always go out by e-mail. */
  emailRequired: boolean;
  email: boolean;
}

export interface NotificationDelivery {
  notificationId: string;
  eventKey: string;
  title: string;
  createdAt: string;
  recipient: { userId: string; email: string | null; name: string | null };
  inApp: { read: boolean } | null;
  email: {
    status: 'sent' | 'skipped' | 'failed';
    providerMessageId: string | null;
    failureReason: string | null;
    attemptedAt: string;
    sentAt: string | null;
  } | null;
}

// Marketplace APIs
export const marketplaceApi = {
  getProviders: async (type: 'lawyer' | 'inspector'): Promise<any[]> => {
    const response = await nestApi.get('/marketplace/providers', { params: { type } });
    return response.data;
  },

  getProvider: async (id: string, type: 'lawyer' | 'inspector'): Promise<any> => {
    const response = await nestApi.get(`/${type}s/${id}`);
    return response.data;
  },

  createRequest: async (data: {
    idempotencyKey: string;
    projectId: string;
    providerType: 'lawyer' | 'inspector';
    providerId: string;
    serviceType: string;
    description: string;
    timeframe: string;
  }): Promise<ProviderRequest> => {
    const response = await nestApi.post('/marketplace/requests', {
      idempotencyKey: data.idempotencyKey,
      projectId: data.projectId,
      providerType: data.providerType,
      providerId: data.providerId,
      serviceType: data.serviceType,
      description: data.description,
      timeframe: data.timeframe,
      message: data.description
    });
    return response.data;
  },

  getProjectRequests: async (projectId: string): Promise<ProviderRequest[]> => {
    const response = await nestApi.get(`/marketplace/requests/project/${projectId}`);
    return response.data;
  },

  getMyRequests: async (): Promise<ProviderRequest[]> => {
    const response = await nestApi.get('/marketplace/requests/my');
    return response.data;
  },

  getAdminAllRequests: async (): Promise<any[]> => {
    const response = await nestApi.get('/marketplace/admin/requests');
    return response.data;
  },

  inviteProvider: async (requestId: string): Promise<any> => {
    const response = await nestApi.post(`/marketplace/requests/${requestId}/invite`);
    return response.data;
  },

  respondToRequest: async (
    requestId: string,
    data: { action: 'accepted' | 'declined'; response?: string }
  ): Promise<ProviderRequest> => {
    const response = await nestApi.post(`/marketplace/requests/${requestId}/respond`, data);
    return response.data;
  },

  getProjectParticipants: async (projectId: string): Promise<any> => {
    const response = await nestApi.get(`/marketplace/projects/${projectId}/participants`);
    return response.data;
  },

  getMyConnectedLawyers: async (): Promise<any[]> => {
    const response = await nestApi.get('/marketplace/my-connected-lawyers');
    return response.data;
  },
  getMyConnectedInspectors: async (): Promise<any[]> => {
    const response = await nestApi.get('/marketplace/my-connected-inspectors');
    return response.data;
  }
};

// Contract Config APIs
export const contractConfigApi = {
  getAll: async (): Promise<any[]> => {
    const response = await nestApi.get('/admin/contract-configs');
    return response.data;
  },
  getById: async (id: string): Promise<any> => {
    const response = await nestApi.get('/admin/contract-configs');
    return response.data.find((config: any) => config.id === id);
  },
  create: async (data: any): Promise<any> => {
    const response = await nestApi.post('/admin/contract-configs', toContractConfigPayload(data));
    return response.data;
  },
  update: async (id: string, data: any): Promise<any> => {
    const response = await nestApi.put(`/admin/contract-configs/${id}`, toContractConfigPayload(data));
    return response.data;
  },
  delete: async (id: string): Promise<void> => {
    await nestApi.post(`/admin/contract-configs/${id}/delete`);
  },
  simulate: async (input: {
    projectValue: number;
    configId: string;
    paymentMethod: 'bank' | 'swish' | 'card';
    includeOneTimeFees: boolean;
  }): Promise<any> => {
    const response = await nestApi.post('/admin/contract-configs/simulate', input);
    return response.data;
  },
  snapshot: async (projectId: string, projectValue: number, configId?: string): Promise<any> => {
    const response = await nestApi.post('/contract-config/snapshot', { projectId, projectValue, configId });
    return response.data;
  },
  getProjectContracts: async (): Promise<any[]> => {
    const response = await nestApi.get('/contract-config/project-contracts');
    return response.data;
  },
  getProjectContract: async (projectId: string): Promise<any> => {
    const response = await nestApi.get(`/contract-config/project-contracts/${projectId}`);
    return response.data;
  },
  /** The bank guide in the deposit (ClickUp 869eu8fd9). */
  getBankGuidance: async (projectId: string, contractId: string, lang: 'sv' | 'en', amountOre?: number): Promise<BankGuidance> => {
    const response = await nestApi.get(`/contract-config/bank-guidance/${projectId}`, { params: { contractId, lang, ...(amountOre !== undefined ? { amountOre } : {}) } });
    return response.data;
  },
  markBankGuidanceSeen: async (projectId: string, contractId: string): Promise<void> => {
    await nestApi.post(`/contract-config/bank-guidance/${projectId}/seen`, { contractId });
  },
  getProjectEconomics: async (projectId: string, method?: string, contractId?: string, milestoneId?: string, amountOre?: number): Promise<any> => {
    const params = new URLSearchParams();
    if (method) params.set('method', method);
    if (contractId) params.set('contractId', contractId);
    if (milestoneId) params.set('milestoneId', milestoneId);
    if (amountOre !== undefined) params.set('amountOre', String(amountOre));
    const qs = params.toString();
    const response = await nestApi.get(`/contract-config/project-economics/${projectId}${qs ? `?${qs}` : ''}`);
    return response.data;
  },
  initiatePayment: async (
    projectId: string,
    paymentMethod: string,
    contractId: string | undefined,
    milestoneId: string | undefined,
    returnUrl: string | undefined,
    policyConsent: CheckoutPolicyConsent,
    options?: { forceNewSession?: boolean; amountOre?: number }
  ): Promise<any> => {
    const response = await nestApi.post('/contract-config/initiate-payment', {
      projectId,
      paymentMethod,
      policyConsent,
      ...(contractId ? { contractId } : {}),
      ...(milestoneId ? { milestoneId } : {}),
      ...(returnUrl ? { returnUrl } : {}),
      ...(options?.forceNewSession ? { forceNewSession: true } : {}),
      ...(options?.amountOre !== undefined ? { amountOre: options.amountOre } : {})
    });
    return response.data;
  },
  registerPaymentInterest: async (projectId: string, paymentMethod: string = 'bank', contractId?: string, milestoneId?: string): Promise<any> => {
    const response = await nestApi.post('/contract-config/payment-interest', {
      projectId,
      paymentMethod,
      ...(contractId ? { contractId } : {}),
      ...(milestoneId ? { milestoneId } : {})
    });
    return response.data;
  },
  completePayment: async (
    projectId: string,
    paymentMethod: string,
    contractId: string | undefined,
    milestoneId: string | undefined,
    policyConsent: CheckoutPolicyConsent
  ): Promise<any> => {
    const response = await nestApi.post('/contract-config/complete-payment', {
      projectId,
      paymentMethod,
      policyConsent,
      ...(contractId ? { contractId } : {}),
      ...(milestoneId ? { milestoneId } : {})
    });
    return response.data;
  },
  syncAdyenCheckoutPayment: async (input: {
    projectId: string;
    paymentMethod?: string;
    contractId?: string;
    milestoneId?: string;
    paymentId?: string;
    sessionId?: string;
    sessionResult?: string;
    redirectResult?: string;
  }): Promise<any> => {
    const response = await nestApi.post('/payments/adyen-checkout/sync', input, { timeout: 10000 });
    return response.data;
  },
  getCheckoutContext: async (projectId: string, contractId?: string): Promise<{ provider: string; mode: string; paymentId?: string }> => {
    const response = await nestApi.get('/payments/checkout-context', { params: { projectId, ...(contractId ? { contractId } : {}) } });
    return response.data;
  },
  releaseMilestonePayout: async (milestoneId: string): Promise<void> => {
    await nestApi.post('/payouts/release', { milestoneId });
  },
  getPingPayouts: async (projectId: string): Promise<PingPayout[]> => {
    const response = await nestApi.get(`/projects/${encodeURIComponent(projectId)}/ping-payouts`);
    return response.data;
  },
  getPingPayoutDebugBank: async (id: string): Promise<PingPayout> => {
    const response = await nestApi.get(`/ping-payouts/${encodeURIComponent(id)}/debug-bank`);
    return response.data;
  },
  actPingPayoutDebugBank: async (id: string, action: 'approve' | 'disburse'): Promise<PingPayout> => {
    const response = await nestApi.post(`/ping-payouts/${encodeURIComponent(id)}/debug-bank/${action}`);
    return response.data;
  },
  getPingDeposit: async (paymentId: string): Promise<{ pingDeposit: PingDeposit }> => {
    const response = await nestApi.get(`/payments/${encodeURIComponent(paymentId)}/ping-deposit`);
    return response.data;
  },
  getPingDebugBank: async (paymentId: string): Promise<PingDebugBank> => {
    const response = await nestApi.get(`/payments/${encodeURIComponent(paymentId)}/debug-bank`);
    return response.data;
  },
  payPingDebugBank: async (paymentId: string): Promise<Pick<PingDebugBank, 'transferStatus'>> => {
    const response = await nestApi.post(`/payments/${encodeURIComponent(paymentId)}/debug-bank/pay`);
    return response.data;
  },
  syncPingDeposit: async (paymentId: string): Promise<{ pingDeposit: PingDeposit }> => {
    const response = await nestApi.post(`/payments/${encodeURIComponent(paymentId)}/ping-deposit/sync`);
    return response.data;
  },
  simulatePingDeposit: async (paymentId: string, input: { amountOre: number; reference: string }): Promise<{ pingDeposit: PingDeposit }> => {
    const response = await nestApi.post(`/payments/${encodeURIComponent(paymentId)}/ping-deposit/mock-transfer`, input);
    return response.data;
  },
  getAdyenRuntime: async (): Promise<PaymentAdyenRuntimeStatus> => {
    const response = await nestApi.get('/payments/adyen-runtime');
    return response.data;
  },

  getPaymentRuntime: async (paymentId: string): Promise<PaymentAdyenRuntimeStatus> => {
    const response = await nestApi.get(`/payments/${encodeURIComponent(paymentId)}/runtime`);
    return response.data;
  },
  getProjectTransactions: async (projectId: string): Promise<any[]> => {
    const response = await nestApi.get(`/payments/projects/${projectId}/timeline`);
    return response.data.items ?? [];
  },
  getProjectPaymentTimeline: async (projectId: string): Promise<any> => {
    const response = await nestApi.get(`/payments/projects/${projectId}/timeline`);
    return response.data;
  },
  getFundingNotifications: async (projectId: string): Promise<import('../types').FundingNotificationStatus[]> => {
    const response = await nestApi.get(`/payments/projects/${projectId}/funding-notifications`);
    return response.data;
  },
  getProjectFunding: async (projectId: string, startingMilestoneIds?: string[]): Promise<import('../types').ProjectFundingStatus> => {
    const preview = startingMilestoneIds?.length ? `?${new URLSearchParams({ startingMilestoneIds: startingMilestoneIds.join(',') })}` : '';
    const response = await nestApi.get(`/payments/projects/${projectId}/funding${preview}`);
    const data = response.data ?? {};
    return {
      ...data,
      contracts: Array.isArray(data.contracts) ? data.contracts : [],
      milestones: Array.isArray(data.milestones)
        ? data.milestones.map((milestone: any) => {
            const grossAmountOre = Number(milestone.grossAmountOre ?? milestone.amountOre ?? 0);
            const rotAmountOre = Number(milestone.rotAmountOre ?? 0);
            return {
              ...milestone,
              grossAmountOre,
              rotAmountOre,
              afterRotAmountOre: Number(
                milestone.afterRotAmountOre ?? grossAmountOre - rotAmountOre
              ),
              rotAllocationMode: milestone.rotAllocationMode ?? 'manual'
            };
          })
        : []
    };
  },
  subscribeProjectPaymentEvents: (
    projectId: string,
    onEvent: (event: ProjectPaymentEvent) => void,
    options?: { onUnavailable?: () => void }
  ): (() => void) => subscribeProjectPaymentEvents(projectId, onEvent, options),
  getAllTransactions: async (): Promise<any[]> => {
    const response = await nestApi.get('/admin/transactions');
    return response.data;
  }
};

export const adyenOnboardingApi = {
  getStatus: async (): Promise<AdyenAccount | null> => {
    const response = await nestApi.get('/adyen/onboarding/status');
    return response.data || null;
  },
  start: async (): Promise<AdyenAccount> => {
    const response = await nestApi.post('/adyen/onboarding/start');
    return response.data;
  },
  refreshStatus: async (): Promise<AdyenAccount | null> => {
    const response = await nestApi.post('/adyen/onboarding/refresh');
    return response.data || null;
  }
};

export const projectStatusApi = {
  getTemplate: async (): Promise<any> => {
    const response = await nestApi.get('/project-status/template');
    return response.data;
  },
  updateTemplate: async (data: { baseSteps: any[]; extraStepPattern: any[] }): Promise<any> => {
    const response = await nestApi.put('/project-status/template', data);
    return response.data;
  },
  previewSteps: async (milestoneCount: number): Promise<any> => {
    const response = await nestApi.get(`/project-status/preview/${milestoneCount}`);
    return response.data;
  },
  getInstance: async (projectId: string): Promise<ProjectStatusInstance> => {
    const response = await nestApi.get(`/projects/${projectId}/status-instance`);
    return response.data;
  },
  updateSubStatus: async (projectId: string, subStatus: string | null): Promise<any> => {
    const response = await nestApi.put(`/projects/${projectId}/status-instance/substatus`, { subStatus });
    return response.data;
  },
  setExceptionStatus: async (projectId: string, status: 'on_hold', message: string): Promise<any> => {
    const response = await nestApi.put(`/projects/${projectId}/status-instance/exception`, { status, message });
    return response.data;
  },
  resumeProject: async (projectId: string, message?: string): Promise<any> => {
    const response = await nestApi.put(`/projects/${projectId}/status-instance/resume`, { message });
    return response.data;
  },
  getStatusMessages: async (projectId: string): Promise<any[]> => {
    const response = await nestApi.get(`/projects/${projectId}/messages`);
    return response.data.filter((message: any) => message.fromStatus && message.toStatus);
  }
};

export default nestApi;


export interface PingOnboardingStatus {
  enabled: boolean;
  contexts: Array<{ id: string; mode: string }>;
  contextId?: string;
  mode?: string;
  merchantStatus?: string;
  kycStatus?: string;
  renewalRequired?: boolean;
  accountStatus?: string;
  accountMasked?: string | null;
  ready?: boolean;
  canSubmit?: boolean;
  missingFields?: string[];
  errorCode?: string | null;
}
export const pingOnboardingApi = {
  status: async (fee = false, contextId?: string): Promise<PingOnboardingStatus> => {
    const response = await nestApi.get(`/ping/onboarding/${fee ? 'fee/' : ''}status`, { params: { contextId } });
    return response.data;
  },
  start: async (fee = false, contextId?: string): Promise<PingOnboardingStatus> => {
    const response = await nestApi.post(`/ping/onboarding/${fee ? 'fee/' : ''}start`, { useTestAccount: true, contextId });
    return response.data;
  },
};


export type PingRefund = {
  id: string;
  paymentId: string;
  sourceContractId: string;
  targetContractId: string;
  amountOre: number;
  state: 'waiting_ais' | 'reserved' | 'distributing' | 'awaiting_payout' | 'confirmed' | 'review_required' | 'returned';
  errorCode: string | null;
  mode: 'mock' | 'sandbox';
  orderId: string;
  creditId: string;
  confirmedAt: string | null;
  history: Array<{ observedAt: string; source: string; state: PingRefund['state'] }>;
};
export const pingRefundApi = {
  list: async (projectId: string): Promise<PingRefund[]> =>
    (await nestApi.get(`/projects/${projectId}/ping-refunds`)).data,
  start: async (paymentId: string) =>
    (await nestApi.post(`/payments/${paymentId}/ping-refund-account/start`)).data,
  simulateAccount: async (paymentId: string) =>
    (await nestApi.post(`/payments/${paymentId}/ping-refund-account/simulate`)).data,
  disburse: async (id: string) =>
    (await nestApi.post(`/ping-refunds/${id}/debug-bank/disburse`)).data,
};
